import { useEffect, useRef, useState } from "react";
import { IoChevronDown, IoRefreshOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import Modal from "../modal/Modal";
import { useAuthStore } from "../../store/authStore";
import {
    ACCOUNT_HARD_STOP_CODES,
    BENEFIT_PLAN_HARD_STOP_CODES,
    COVERAGE_TIER_SET_HARD_STOP_CODES,
    COVERAGE_TIER_SET_NAMES,
    VALIDATION_SEVERITY,
    applyFetchedCategoryLookups,
    applyComponentCollisionAmendments,
    applyHrpCoverageTierSets,
    applyRateCollisionAmendments,
    applyRateCollisionTransactionTypes,
    createValidationCollector,
    createValidationError,
    extractBillingRateAccountHccId,
    extractBillingRateAsOfDate,
    resolveBillingRateAsOfDate,
    fetchBillingRateUploadContext,
    filterBillableRenewalFees,
    filterBillablePrimaryFees,
    filterAsaNetworkAccessFees,
    dedupePrimaryFees,
    applyDocumentReconciliation,
    syncBillingRatePayloadsFromComponents,
    mapToBillingComponents,
    normalizeParsedFee,
    MAX_SOLD_PLAN_COUNT,
    parseSoldRateWorkbook,
    prepareHrpReconciliationContext,
    promoteRenewalDatesToGroup,
    resolveHrpBenefitPlanName,
    sanitizeImportedComponentAndRateIdentifiers,
    stampAsOfDateOnComponentPayloads,
    toValidationError,
    validateBillingRateAccountAndPlans,
    fetchRateCollisions,
    enrichRatePayloadsForHrpSubmit,
    buildAccountNameSearchCandidates,
} from "../../utils/billingRateUtils.js";
import { DRAFT_SAVED_EVENT, DRAFT_PRUNED_EVENT, clearDrafts, pruneDraftsOnLoad } from "../../utils/billingRateDraftStorage.js";
import {
    fetchCoverageTierSetByBenefitPlan,
    fetchBillingRateCategories,
    fetchBillingRateSubCategories,
    checkBillingRateComponentCollisions,
    fetchBillingRateByBenefitPlanKey,
    fetchBillingRateComponentsByAccountKey,
    fetchBillingRateAndComponents,
    fetchAccounts,
    fetchBenefitPlansByAccountKey,
    createBillingRateSubmission,
    createBillingRateSubmissionAsync,
    appendFilesToBillingRateSubmission,
    removeFilesFromBillingRateSubmission,
    fetchBillingRatePresignedUrl,
    fetchBillingRateSubmission,
    fetchBillingRateSubmissions,
    deleteBillingRateSubmission,
    submitBillingRateAudit,
} from "../../utils/apiUtils.js";

const RowMenu = ({ items }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const rect = triggerRef.current?.getBoundingClientRect();
        if (rect) setPos({ top: rect.bottom + 4, left: rect.right });
        const handler = (e) => {
            if (
                triggerRef.current && !triggerRef.current.contains(e.target) &&
                dropdownRef.current && !dropdownRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const visibleItems = items.filter(Boolean);
    const disabled = visibleItems.length === 0;

    return (
        <div className="billing-rate-builder__row-menu">
            <button
                ref={triggerRef}
                type="button"
                className="billing-rate-builder__row-menu-trigger"
                onClick={() => !disabled && setOpen((v) => !v)}
                title="More options"
                disabled={disabled}
            >
                ⋮
            </button>
            {open && (
                <ul
                    ref={dropdownRef}
                    className="billing-rate-builder__row-menu-dropdown"
                    style={{ position: "fixed", top: pos.top, left: pos.left, transform: "translateX(-100%)" }}
                >
                    {visibleItems.map((item) => (
                        <li key={item.label}>
                            <button
                                type="button"
                                className={`billing-rate-builder__row-menu-item${item.danger ? " billing-rate-builder__row-menu-item--danger" : ""}`}
                                onClick={() => { setOpen(false); item.onClick(); }}
                            >
                                {item.label}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const STEP = 1;


const PROCESSING_POLL_INTERVAL_MS = 10_000;

const STATUS = {
    QUEUED: "queued",
    PROCESSING: "processing",
    DONE: "done",
    ERROR: "error",
    SUBMITTED: "submitted",
};

const FILE_TYPE = { XLSX: "xlsx", PDF: "pdf", UNSUPPORTED: "unsupported" };

const ASO_FILE_TYPE = {
    STOPLOSS_CONTRACT: "STOPLOSS_CONTRACT",
    ASA_CONTRACT: "ASA_CONTRACT",
    RENEWAL_CONFIRMATION: "RENEWAL_CONFIRMATION",
    MISCELLANEOUS: "MISCELLANEOUS",
    MCW: "MCW",
};

const EXCEL_FILE_ACCEPT = ".xlsx,.xlsm,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.ms-excel";
const ACCEPTED_XLSX = EXCEL_FILE_ACCEPT;
const ACCEPTED_ASO_XLSX = EXCEL_FILE_ACCEPT;
// ASO documents (AI/Textract path) accept PDFs only; Sold Rates workbooks go through the local Excel picker.
const ACCEPTED_ASO_FILES = ".pdf,application/pdf";

const PLAN_TYPE_LABELS = { "level-funded": "Level-Funded", aso: "ASO" };
const planTypeLabel = (planType) => PLAN_TYPE_LABELS[planType] ?? planType;

const COVERAGE_TIER_SET_OPTIONS = Object.values(COVERAGE_TIER_SET_NAMES);
const COVERAGE_TIER_HARD_STOP_SET = new Set(COVERAGE_TIER_SET_HARD_STOP_CODES);
const ACCOUNT_HARD_STOP_SET = new Set(ACCOUNT_HARD_STOP_CODES);
const BENEFIT_PLAN_HARD_STOP_SET = new Set(BENEFIT_PLAN_HARD_STOP_CODES);

function unresolvedCoverageTierHardStops(hardStops, selections = {}) {
    return hardStops.filter((error) => (
        COVERAGE_TIER_HARD_STOP_SET.has(error.code)
        && error.benefitPlanHccId
        && !selections[error.benefitPlanHccId]
    ));
}

function coverageTierResolutionPlans(hardStops) {
    const seen = new Map();
    hardStops.forEach((error) => {
        const key = error.benefitPlanHccId;
        if (!key || seen.has(key)) return;
        seen.set(key, {
            index: error.plan?.index,
            name: error.plan?.name,
            benefitPlanHccId: key,
            message: error.message,
        });
    });
    return Array.from(seen.values());
}

function unresolvedBenefitPlanHardStops(hardStops, planSelections = {}) {
    return hardStops.filter((error) => (
        BENEFIT_PLAN_HARD_STOP_SET.has(error.code)
        && error.plan?.index != null
        && !planSelections[error.plan.index]
    ));
}

function benefitPlanResolutionPlans(hardStops) {
    const seen = new Map();
    hardStops.forEach((error) => {
        const key = error.plan?.index;
        if (key == null || seen.has(key)) return;
        seen.set(key, {
            index: error.plan.index,
            planName: error.planName ?? error.plan?.name ?? null,
            benefitPlanHccId: error.benefitPlanHccId,
            message: error.message,
        });
    });
    return Array.from(seen.values());
}

function isExcelWorkbookFileName(name = "") {
    const lower = name.toLowerCase();
    return lower.endsWith(".xlsx") || lower.endsWith(".xlsm") || lower.endsWith(".xls");
}

function detectFileType(file) {
    const name = file.name.toLowerCase();
    if (isExcelWorkbookFileName(name)) return FILE_TYPE.XLSX;
    if (name.endsWith(".pdf")) return FILE_TYPE.PDF;
    return FILE_TYPE.UNSUPPORTED;
}

function detectAsoFileTypeFromName(name = "") {
    const lower = name.toLowerCase();
    if (isExcelWorkbookFileName(lower)) return ASO_FILE_TYPE.MCW;
    if (isStopLossFile(name)) return ASO_FILE_TYPE.STOPLOSS_CONTRACT;
    if (isAsaContractFile(name)) return ASO_FILE_TYPE.ASA_CONTRACT;
    if (isRenewalConfirmationFile(name)) return ASO_FILE_TYPE.RENEWAL_CONFIRMATION;
    return ASO_FILE_TYPE.MISCELLANEOUS;
}

function detectAsoFileType(file) {
    return detectAsoFileTypeFromName(file.name);
}

function isStopLossFile(name) {
    return /^stoploss_contract_/i.test(name ?? "");
}

function isAsaContractFile(name) {
    return /^asa_contract_/i.test(name ?? "");
}

function isRenewalConfirmationFile(name) {
    return /^renewal_confirmation_/i.test(name ?? "");
}

async function extractParsed(file, parseOptions = {}) {
    if (detectFileType(file) !== FILE_TYPE.XLSX) {
        throw Object.assign(
            new Error(`Unsupported file type: ${file.name}`),
            { code: "UNSUPPORTED_FILE_TYPE" },
        );
    }
    const arrayBuffer = await file.arrayBuffer();
    return { parsed: parseSoldRateWorkbook(arrayBuffer, parseOptions), s3Key: null };
}

function normalizeFees(fees, { reindex = false, planType } = {}) {
    return (fees ?? []).map((fee, i) =>
        normalizeParsedFee(reindex ? { ...fee, rowNumber: i + 1 } : fee, { planType }),
    );
}

function mergeParsedResults(parsedArray, { planType } = {}) {
    if (parsedArray.length === 0) return null;
    if (parsedArray.length === 1) {
        const r = promoteRenewalDatesToGroup(parsedArray[0]);
        return { ...r, fees: normalizeFees(r.fees, { planType }) };
    }
    const merged = {
        sheetName: parsedArray[0].sheetName,
        group: parsedArray.reduce((acc, { group: g = {} }) => ({
            name: acc.name ?? g.name ?? null,
            code: acc.code ?? g.code ?? null,
            soldPlanCount: acc.soldPlanCount ?? g.soldPlanCount ?? null,
            effectiveDate: acc.effectiveDate ?? g.effectiveDate ?? null,
            billingPeriodStart: acc.billingPeriodStart ?? g.billingPeriodStart ?? null,
            billingPeriodEnd: acc.billingPeriodEnd ?? g.billingPeriodEnd ?? null,
        }), {}),
        plans: parsedArray.flatMap((r) => r.plans ?? []),
        fees: normalizeFees(parsedArray.flatMap((r) => r.fees ?? []), { reindex: true, planType }),
    };
    return promoteRenewalDatesToGroup(merged);
}

function mapProcessingStatus(ps) {
    if (ps === "DONE") return STATUS.DONE;
    if (ps === "FAILED") return STATUS.ERROR;
    return STATUS.PROCESSING;
}

function mapHistoryItems(dbItems) {
    return (dbItems ?? []).flatMap((item) => {
        // New async format
        if (Array.isArray(item.files)) {
            const base = {
                clientName: item.clientName ?? null,
                createdAt: new Date(item.createdAt),
                submittedBy: item.submittedBy ?? null,
                result: null,
                dbRecord: { pk: item.pk, sk: item.sk },
                source: "history",
                isAsyncSubmission: true,
                planType: "aso",
            };
            return item.files.map((f, i) => {
                const fileStatus = item.status === "submitted"
                    ? STATUS.SUBMITTED
                    : mapProcessingStatus(f.processingStatus);
                return {
                    ...base,
                    submittedBy: f.submittedBy ?? base.submittedBy,
                    id: `${item.sk}#${i}`,
                    groupId: item.sk,
                    name: f.fileName,
                    type: f.fileType === "MCW" ? FILE_TYPE.XLSX : FILE_TYPE.PDF,
                    s3Keys: [f.s3Key],
                    status: fileStatus,
                    errorMsg: f.processingStatus === "FAILED" ? (f.error ?? "Processing failed") : null,
                    parsed: null,
                };
            });
        }

        // Old format
        const s3Keys = item.s3Keys ?? [];
        const base = {
            type: item.fileType,
            planType: item.planType ?? "level-funded",
            clientName: item.clientName ?? null,
            createdAt: new Date(item.createdAt),
            status: item.status === "submitted" ? STATUS.SUBMITTED : STATUS.DONE,
            submittedBy: item.submittedBy ?? null,
            result: null,
            errorMsg: null,
            dbRecord: { pk: item.pk, sk: item.sk },
            source: "history",
        };
        if (s3Keys.length > 1) {
            const fileNames = (item.fileName || "").split(", ");
            return s3Keys.map((s3Key, i) => ({
                ...base,
                id: `${item.sk}#${i}`,
                groupId: item.sk,
                name: fileNames[i] ?? s3Key,
                s3Keys: [s3Key],
            }));
        }
        return [{ ...base, id: item.sk, groupId: item.sk, name: item.fileName, s3Keys }];
    });
}

function groupItemsByClient(items) {
    const map = new Map();
    items.forEach((item) => {
        const key = item.groupId ?? item.clientName ?? "—";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
    });
    return map;
}

function getDeletableSubmissionSks(clientItems) {
    return [...new Set(clientItems.filter((i) => i.dbRecord?.sk).map((i) => i.dbRecord.sk))];
}

function canDeleteSubmission(clientItems) {
    return getDeletableSubmissionSks(clientItems).length > 0
        || clientItems.some((i) => i.source === "session");
}

function getSubmissionSk(clientItems) {
    return clientItems.find((i) => i.dbRecord?.sk)?.dbRecord?.sk ?? null;
}

function canManageAsoFiles({ isLevelFunded, overallStatus, isAsyncGroup, asyncStatus, clientItems }) {
    if (isLevelFunded || overallStatus === STATUS.SUBMITTED) return false;
    if (isAsyncGroup && (asyncStatus === "PENDING" || asyncStatus === "PROCESSING")) return false;
    return Boolean(getSubmissionSk(clientItems)) || clientItems.some((i) => i.source === "session");
}

function canRemoveAsoFile(item, canManage) {
    if (!canManage || item.type === "json") return false;
    if (item.status === STATUS.PROCESSING || item.status === STATUS.SUBMITTED) return false;
    return true;
}

function findLatestAsoGroupForClient(clientName, items) {
    if (!clientName) return null;
    const lower = clientName.toLowerCase();
    const byGroup = new Map();
    items.forEach((item) => {
        if (item.planType !== "aso") return;
        if ((item.clientName ?? "").toLowerCase() !== lower) return;
        if (!byGroup.has(item.groupId)) byGroup.set(item.groupId, []);
        byGroup.get(item.groupId).push(item);
    });
    if (byGroup.size === 0) return null;
    let latestGroupId = null;
    let latestDate = null;
    byGroup.forEach((groupItems, groupId) => {
        const newest = groupItems.reduce((a, b) => (b.createdAt > a.createdAt ? b : a));
        if (!latestDate || newest.createdAt > latestDate) {
            latestDate = newest.createdAt;
            latestGroupId = groupId;
        }
    });
    const groupItems = byGroup.get(latestGroupId);
    const rep = groupItems.reduce((a, b) => (b.createdAt > a.createdAt ? b : a));
    const sk = rep.dbRecord?.sk ?? null;
    return {
        groupId: latestGroupId,
        sk,
        ref: sk ?? latestGroupId,
        clientName: rep.clientName,
        createdAt: rep.createdAt,
        fileNames: groupItems.map((i) => i.name),
        submittedBy: rep.submittedBy,
    };
}

const StatusBadge = ({ status }) => {
    const config = {
        [STATUS.QUEUED]: { label: "Queued", mod: "queued" },
        [STATUS.PROCESSING]: { label: "Processing…", mod: "processing" },
        [STATUS.DONE]: { label: "Ready", mod: "done" },
        [STATUS.SUBMITTED]: { label: "Submitted", mod: "submitted" },
        [STATUS.ERROR]: { label: "Error", mod: "error" },
    };
    const { label, mod } = config[status] ?? { label: status, mod: "" };
    return (
        <span className={`billing-rate-builder__status-badge billing-rate-builder__status-badge--${mod}`}>
            {label}
        </span>
    );
};

const BillingRateBuilderStepOne = ({
    activeStep,
    onComponentPayloadsExtracted,
    onRatePayloadsExtracted,
    onFeesExtracted,
    onPlansExtracted,
    onUploadContextExtracted,
    onSubCategoryRowsExtracted,
    onBillingCategoryRowsExtracted,
    onComponentCollisionsExtracted,
    onRateCollisionsExtracted,
    onHrpSnapshotExtracted,
    onHrpReconciliationExtracted,
    onProceed,
    onImport,
    onProceedToNext,
    onGroupIdExtracted,
    onPlanTypeExtracted,
    onRegisterMarkSubmitted,
    getDraftsMeta,
    onResumeDraft,
}) => {
    const { email: currentUserEmail } = useAuthStore();
    const xlsxInputRef = useRef(null);
    const asoXlsxInputRef = useRef(null);
    const pdfInputRef = useRef(null);
    const importJsonInputRef = useRef(null);
    const asoDebounceRef = useRef(null);
    const asoWrapperRef = useRef(null);
    const accountSearchDebounceRef = useRef(null);
    const accountSearchWrapperRef = useRef(null);
    const pollIntervalRef = useRef({});
    const [mode, setMode] = useState("upload");
    const [planType, setPlanType] = useState("level-funded");
    const [levelFundedClientName, setLevelFundedClientName] = useState("");
    const [asoClientName, setAsoClientName] = useState("");
    const [asoSource, setAsoSource] = useState("spreadsheet"); // 'spreadsheet' | 'documents'
    const [asoSelectedAccount, setAsoSelectedAccount] = useState(null);
    const [asoDropdownItems, setAsoDropdownItems] = useState([]);
    const [asoDropdownLoading, setAsoDropdownLoading] = useState(false);
    const [asoDropdownOpen, setAsoDropdownOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [tableFilter, setTableFilter] = useState("all");
    const [expandedClients, setExpandedClients] = useState(new Set());
    const [proceedingClient, setProceedingClient] = useState(null);
    const [validationAlert, setValidationAlert] = useState(null);
    const [mergedClients, setMergedClients] = useState(new Set());
    const [pendingPicker, setPendingPicker] = useState(null);
    const [pendingGroupId, setPendingGroupId] = useState(null);
    const [pendingRemoveIds, setPendingRemoveIds] = useState([]);
    const [submissionStatusByGroupId, setSubmissionStatusByGroupId] = useState({});
    const [extractedDataByGroupId, setExtractedDataByGroupId] = useState({});
    const [coverageTierResolution, setCoverageTierResolution] = useState(null);
    const [coverageTierSelections, setCoverageTierSelections] = useState({});
    const [accountResolution, setAccountResolution] = useState(null);
    const [accountSelections, setAccountSelections] = useState({});
    const [planResolution, setPlanResolution] = useState(null);
    const [planSelections, setPlanSelections] = useState({});
    const [accountSearchTerm, setAccountSearchTerm] = useState("");
    const [accountSearchItems, setAccountSearchItems] = useState([]);
    const [accountSearchLoading, setAccountSearchLoading] = useState(false);
    const [accountSearchOpen, setAccountSearchOpen] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [fileDeleteConfirm, setFileDeleteConfirm] = useState(null);
    const [fileDeleteLoading, setFileDeleteLoading] = useState(false);
    const [pendingAddClientName, setPendingAddClientName] = useState(null);
    const [duplicateCheck, setDuplicateCheck] = useState(null);
    const [submittedGroupIds, setSubmittedGroupIds] = useState(new Set());
    const [completedSectionCollapsed, setCompletedSectionCollapsed] = useState(true);
    const [inProgressSectionCollapsed, setInProgressSectionCollapsed] = useState(false);
    const [inProgressPage, setInProgressPage] = useState(1);
    const [completedPage, setCompletedPage] = useState(1);
    const [draftsMeta, setDraftsMeta] = useState({});
    const [draftCheckPending, setDraftCheckPending] = useState(null); // { groupId, clientItems, draftId, allowStartFresh, isJsonImport }
    const [soldPlanCountPrompt, setSoldPlanCountPrompt] = useState(null); // { entries, value }
    const [filePickerLoading, setFilePickerLoading] = useState(null); // null | 'xlsx' | 'aso' | 'json'

    useEffect(() => {
        onRegisterMarkSubmitted?.((groupId) => {
            setSubmittedGroupIds((prev) => new Set([...prev, groupId]));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (getDraftsMeta) setDraftsMeta(getDraftsMeta());
    }, [getDraftsMeta, activeStep]);

    useEffect(() => {
        const refreshDraftsMeta = () => {
            if (getDraftsMeta) setDraftsMeta(getDraftsMeta());
        };
        window.addEventListener(DRAFT_SAVED_EVENT, refreshDraftsMeta);
        window.addEventListener(DRAFT_PRUNED_EVENT, refreshDraftsMeta);
        return () => {
            window.removeEventListener(DRAFT_SAVED_EVENT, refreshDraftsMeta);
            window.removeEventListener(DRAFT_PRUNED_EVENT, refreshDraftsMeta);
        };
    }, [getDraftsMeta]);

    // Polling helpers — all dependencies are stable React state setters or refs
    function pollSubmission(groupId, sk) {
        fetchBillingRateSubmission(sk).then((record) => {
            const { processingStatus, extractedData, files: dbFiles } = record;

            if (!processingStatus) {
                clearInterval(pollIntervalRef.current[groupId]);
                delete pollIntervalRef.current[groupId];
                setSubmissionStatusByGroupId((prev) => {
                    const next = { ...prev };
                    delete next[groupId];
                    return next;
                });
                return;
            }

            setSubmissionStatusByGroupId((prev) => ({ ...prev, [groupId]: processingStatus }));

            if (processingStatus === "EXTRACTION_COMPLETE") {
                clearInterval(pollIntervalRef.current[groupId]);
                delete pollIntervalRef.current[groupId];
                setExtractedDataByGroupId((prev) => ({ ...prev, [groupId]: extractedData }));
                setItems((prev) =>
                    prev.map((item) =>
                        item.dbRecord?.sk === sk ? { ...item, status: STATUS.DONE } : item
                    )
                );
            } else if (processingStatus === "FAILED") {
                clearInterval(pollIntervalRef.current[groupId]);
                delete pollIntervalRef.current[groupId];
                setItems((prev) =>
                    prev.map((item) => {
                        if (item.dbRecord?.sk !== sk) return item;
                        const fileEntry = dbFiles?.find((f) => f.fileName === item.name);
                        return { ...item, status: STATUS.ERROR, errorMsg: fileEntry?.error ?? "Processing failed" };
                    })
                );
            }
        }).catch(() => {});
    }

    function startPolling(groupId, sk) {
        pollSubmission(groupId, sk);
        if (pollIntervalRef.current[groupId]) clearInterval(pollIntervalRef.current[groupId]);
        pollIntervalRef.current[groupId] = setInterval(() => pollSubmission(groupId, sk), PROCESSING_POLL_INTERVAL_MS);
    }

    function syncSubmissionAfterFileChange(groupId, sk, record) {
        const { processingStatus, extractedData } = record;
        setSubmissionStatusByGroupId((prev) => ({ ...prev, [groupId]: processingStatus }));
        setExtractedDataByGroupId((prev) => {
            const next = { ...prev };
            if (processingStatus === "EXTRACTION_COMPLETE" && extractedData) {
                next[groupId] = extractedData;
            } else {
                delete next[groupId];
            }
            return next;
        });
        if (processingStatus === "EXTRACTION_COMPLETE") {
            setItems((prev) =>
                prev.map((item) =>
                    item.dbRecord?.sk === sk
                        ? { ...item, status: STATUS.DONE, errorMsg: null }
                        : item
                )
            );
        } else if (processingStatus === "FAILED") {
            setItems((prev) =>
                prev.map((item) =>
                    item.dbRecord?.sk === sk && item.status !== STATUS.ERROR
                        ? { ...item, status: STATUS.ERROR, errorMsg: "Processing failed" }
                        : item
                )
            );
        }
        if (processingStatus === "PENDING" || processingStatus === "PROCESSING") {
            startPolling(groupId, sk);
        }
    }

    const handleRefresh = () => {
        fetchBillingRateSubmissions()
            .then((result) => {
                const historyItems = mapHistoryItems(result.Items)
                    .sort((a, b) => b.createdAt - a.createdAt);
                setItems((prev) => [
                    ...prev.filter((i) => i.source === "session"),
                    ...historyItems,
                ]);

                (result.Items ?? []).forEach((item) => {
                    if (!Array.isArray(item.files)) return;
                    const groupId = item.sk;
                    const ps = item.processingStatus;
                    setSubmissionStatusByGroupId((prev) => ({ ...prev, [groupId]: ps }));
                    if (ps === "EXTRACTION_COMPLETE" && item.extractedData) {
                        setExtractedDataByGroupId((prev) => ({ ...prev, [groupId]: item.extractedData }));
                    } else if ((ps === "PENDING" || ps === "PROCESSING") && !pollIntervalRef.current[groupId]) {
                        startPolling(groupId, item.sk);
                    }
                });
            })
            .catch(() => {});
    };

    const handleImportJsonFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFilePickerLoading("json");
        const reader = new FileReader();
        reader.onload = async (ev) => {
            try {
                const parsed = JSON.parse(ev.target.result);
                if (!parsed.componentPayloads || !parsed.ratePayloads) {
                    throw new Error("Invalid file: missing componentPayloads or ratePayloads.");
                }
                const componentPayloads = parsed.componentPayloads;
                // Imported files may carry raw component names/IDs from whatever
                // produced them, so normalize whitespace the same way the extraction
                // pipeline and manual edits already do before anything (collision
                // checks, step two) reads these values.
                sanitizeImportedComponentAndRateIdentifiers(componentPayloads, parsed.ratePayloads);
                const importedClientName = parsed.clientName
                    ?? parsed.ratePayloads?.[0]?.accountName
                    ?? null;
                const importedPlanType = parsed.planType ?? null;
                const accountHccId = extractBillingRateAccountHccId({
                    ratePayloads: parsed.ratePayloads,
                    componentPayloads,
                });
                const asOfDate = componentPayloads[0]?.billingRateComponentPayload?.asOfDate ?? null;

                let resolvedImportAccount = null;
                if (accountHccId) {
                    const byHccId = await fetchAccounts({ accountHccId, limit: 1 }).catch(() => null);
                    resolvedImportAccount = byHccId?.rows?.[0] ?? null;
                }
                if (!resolvedImportAccount && importedClientName) {
                    const byName = await fetchAccounts({ accountName: importedClientName, limit: 1 }).catch(() => null);
                    resolvedImportAccount = byName?.rows?.[0] ?? null;
                }
                // Without an account in this environment nothing in the file can be
                // re-checked against HRP, so stop before any submission row is created.
                if (!resolvedImportAccount?.accountKey) {
                    throw new Error(`Account "${accountHccId ?? importedClientName ?? "unknown"}" from the import file was not found in this environment.`);
                }

                const collisionCandidates = componentPayloads.map((wrapper) => {
                    const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
                    return {
                        componentName: component?.componentName ?? "",
                        componentId: component?.definition?.rateClassifications?.componentId ?? "",
                    };
                });
                const componentCollisions = collisionCandidates.length > 0
                    ? await checkBillingRateComponentCollisions(collisionCandidates).catch(() => null)
                    : null;
                const globalCollisionRows = Array.isArray(componentCollisions?.rows)
                    ? componentCollisions.rows
                    : [];

                const hrpContext = await prepareHrpReconciliationContext({
                    componentPayloads,
                    fees: parsed.fees ?? [],
                    ratePayloads: parsed.ratePayloads ?? [],
                    planType: importedPlanType ?? 'aso',
                    accountHccId,
                    clientName: importedClientName,
                    asOfDate,
                    accountOverride: resolvedImportAccount,
                    globalCollisionRows,
                    fetchAccounts,
                    fetchBenefitPlansByAccountKey,
                    fetchBillingRateComponentsByAccountKey,
                    fetchBillingRateAndComponents,
                });

                if (!hrpContext.hasHrpReconciliation) {
                    applyComponentCollisionAmendments(
                        componentPayloads,
                        Array.isArray(componentCollisions?.rows) ? componentCollisions.rows : [],
                        hrpContext.groupId ?? accountHccId,
                    );
                }

                const accountBenefitPlans = await fetchBenefitPlansByAccountKey({
                    accountKey: resolvedImportAccount.accountKey,
                }).catch(() => []);
                // The file's own rateCollisions are never trusted: they describe the
                // environment the export came from. An empty plan list means the check
                // could not run, which is not the same as "no collisions".
                if (!accountBenefitPlans.some((plan) => plan?.benefitPlanStatus === 'a')) {
                    throw new Error(`No active benefit plans were found for account "${resolvedImportAccount.accountHccId ?? accountHccId}" in this environment, so rate transaction types could not be verified.`);
                }
                enrichRatePayloadsForHrpSubmit(parsed.ratePayloads, accountBenefitPlans);

                const unmappedPlans = [...new Set(
                    (parsed.ratePayloads ?? [])
                        .map((wrapper) => wrapper?.benefitPlan ?? wrapper?.plan?.name ?? null)
                        .filter((hccId) => hccId && !resolveHrpBenefitPlanName(hccId, accountBenefitPlans)),
                )];
                if (unmappedPlans.length > 0) {
                    toast.error(`These benefit plans from the file are not active on this account in this environment and will fail on submit: ${unmappedPlans.join(", ")}.`, { autoClose: 10000, className: "billing-rate-builder__toast--error" });
                }

                const rateCollisions = await fetchRateCollisions(
                    accountBenefitPlans,
                    parsed.ratePayloads,
                    fetchBillingRateByBenefitPlanKey,
                );
                applyRateCollisionTransactionTypes(parsed.ratePayloads, rateCollisions);

                const tierSetCheck = await applyHrpCoverageTierSets({
                    componentPayloads,
                    plans: parsed.plans ?? [],
                    fetchCoverageTierSetByBenefitPlan,
                });
                if (tierSetCheck.unverified.length > 0) {
                    toast.error(`Coverage tier sets could not be verified and must be set manually in step 2: ${tierSetCheck.unverified.join("; ")}.`, { autoClose: 15000, className: "billing-rate-builder__toast--error" });
                }

                // Find matching submission row by clientName + planType
                const normalizedImportClient = (importedClientName ?? "").toLowerCase();
                const grouped = groupItemsByClient(items);
                let matchedTableGroupId = null;
                let matchedDisplayName = null;
                let matchedDate = null;
                for (const [gId, clientItems] of grouped.entries()) {
                    const rowClient = (clientItems[0]?.clientName ?? "").toLowerCase();
                    const rowPlanType = clientItems[0]?.planType ?? null;
                    // Skip already-submitted groups — a fresh import should not reuse a submitted row
                    const alreadySubmitted = submittedGroupIds.has(gId) || clientItems.every((i) => i.status === STATUS.SUBMITTED);
                    if (normalizedImportClient && rowClient === normalizedImportClient && rowPlanType === importedPlanType && !alreadySubmitted) {
                        matchedTableGroupId = gId;
                        matchedDisplayName = clientItems[0]?.clientName ?? gId;
                        matchedDate = clientItems[0]?.createdAt;
                        break;
                    }
                }

                // No match — create a real DB record so the entry survives a page refresh
                if (!matchedTableGroupId) {
                    const dbRecord = await createBillingRateSubmission({
                        clientName: importedClientName ?? "",
                        fileName: file.name,
                        fileType: "json",
                        planType: importedPlanType ?? "level-funded",
                        componentCount: componentPayloads.length,
                        rateCount: parsed.ratePayloads?.length ?? 0,
                        s3Keys: [],
                    }).catch(() => null);

                    const resolvedGroupId = dbRecord?.sk ?? crypto.randomUUID();
                    const newItem = {
                        id: crypto.randomUUID(),
                        groupId: resolvedGroupId,
                        name: file.name,
                        type: "json",
                        planType: importedPlanType ?? "level-funded",
                        clientName: importedClientName,
                        createdAt: new Date(),
                        status: STATUS.DONE,
                        parsed: null,
                        errorMsg: null,
                        result: null,
                        dbRecord: dbRecord ? { pk: dbRecord.pk, sk: dbRecord.sk } : null,
                        source: "session",
                        submittedBy: null,
                        s3Keys: [],
                        isAsyncSubmission: false,
                    };
                    setItems((prev) => [newItem, ...prev]);
                    if (importedPlanType) setTableFilter(importedPlanType);
                    toast.info(`No existing submission found for "${importedClientName ?? "unknown client"}". A new entry has been created in the table.`, { autoClose: 6000, className: "billing-rate-builder__toast--info" });
                    matchedTableGroupId = resolvedGroupId;
                }

                const [billingRateCategories, billingRateSubCategories] = await Promise.all([
                    fetchBillingRateCategories().catch(() => null),
                    fetchBillingRateSubCategories().catch(() => null),
                ]);
                // The imported file's subCategoryMatch was resolved against the source
                // environment's code table and may not exist in this one (e.g. a UAT-only
                // rate sub category). Re-resolve against this environment's rows so a
                // missing match surfaces as a validation error instead of an HRP submit failure.
                applyFetchedCategoryLookups(
                    componentPayloads,
                    parsed.ratePayloads,
                    { subCategories: billingRateSubCategories, billingCategories: billingRateCategories },
                );
                onSubCategoryRowsExtracted?.(Array.isArray(billingRateSubCategories) ? billingRateSubCategories : []);
                onBillingCategoryRowsExtracted?.(Array.isArray(billingRateCategories) ? billingRateCategories : []);
                onHrpSnapshotExtracted?.(hrpContext.hrpSnapshot);
                onHrpReconciliationExtracted?.(hrpContext.hrpReconciliation);
                onGroupIdExtracted?.(hrpContext.groupId ?? accountHccId);
                if (importedPlanType) onPlanTypeExtracted?.(importedPlanType);

                onImport?.({
                    componentPayloads,
                    ratePayloads: parsed.ratePayloads,
                    fees: parsed.fees,
                    plans: parsed.plans,
                    uploadContext: tierSetCheck.uploadContext,
                    componentCollisions: Array.isArray(componentCollisions?.rows) ? componentCollisions.rows : [],
                    rateCollisions,
                    planType: importedPlanType,
                    clientName: importedClientName,
                    tableGroupId: matchedTableGroupId,
                    hrpReconciliationSettled: false,
                });
                onProceedToNext?.();
            } catch (err) {
                toast.error(err.message ?? "Failed to parse import file.", { className: "billing-rate-builder__toast--error" });
            } finally {
                setFilePickerLoading(null);
                e.target.value = "";
            }
        };
        reader.onerror = () => {
            setFilePickerLoading(null);
            toast.error("Failed to read import file.", { className: "billing-rate-builder__toast--error" });
            e.target.value = "";
        };
        reader.readAsText(file);
    };

    useEffect(() => {
        if (!pendingPicker) return;
        if (pendingPicker === "xlsx") xlsxInputRef.current?.click();
        else if (pendingPicker === "pdf") pdfInputRef.current?.click();
        setPendingPicker(null);
    }, [pendingPicker]);

    useEffect(() => {
        pruneDraftsOnLoad();
        handleRefresh();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (asoWrapperRef.current && !asoWrapperRef.current.contains(e.target)) {
                setAsoDropdownOpen(false);
            }
            if (accountSearchWrapperRef.current && !accountSearchWrapperRef.current.contains(e.target)) {
                setAccountSearchOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        return () => {
            Object.values(pollIntervalRef.current).forEach(clearInterval);
        };
    }, []);

    const handleAsoClientNameChange = (e) => {
        const value = e.target.value;
        setAsoClientName(value);
        setAsoSelectedAccount(null);
        clearTimeout(asoDebounceRef.current);
        if (!value.trim()) {
            setAsoDropdownItems([]);
            setAsoDropdownOpen(false);
            return;
        }
        asoDebounceRef.current = setTimeout(() => {
            setAsoDropdownLoading(true);
            fetchAccounts({ accountName: value, limit: 10 })
                .then((result) => {
                    setAsoDropdownItems(result?.rows ?? []);
                    setAsoDropdownOpen(true);
                })
                .catch(() => setAsoDropdownItems([]))
                .finally(() => setAsoDropdownLoading(false));
        }, 300);
    };

    const handleAsoClientSelect = (row) => {
        setAsoClientName(row.accountName);
        setAsoSelectedAccount(row);
        setAsoDropdownOpen(false);
        setAsoDropdownItems([]);
    };

    if (activeStep !== STEP) return null;

    const updateItem = (id, updates) => {
        setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } : item)));
    };

    // ─── Local XLSX extraction (level-funded and ASO Sold Rates share this sync flow) ──
    const handleFiles = (files, { clientName = "", groupId = null, replaceIds = [], recordPlanType = "level-funded", extractionMode = "spreadsheet", parseOptions = {} } = {}) => {
        const batchGroupId = groupId ?? crypto.randomUUID();
        const submissionSk = groupId
            ? items.find((i) => i.groupId === groupId && i.dbRecord?.sk)?.dbRecord?.sk ?? null
            : null;
        setValidationAlert(null);
        setCoverageTierResolution(null);
        setCoverageTierSelections({});
        setAccountResolution(null);
        setAccountSelections({});
        setPlanResolution(null);
        setPlanSelections({});
        setAccountSearchTerm("");
        setAccountSearchItems([]);
        setAccountSearchOpen(false);
        const newItems = Array.from(files).map((file) => ({
            id: crypto.randomUUID(),
            groupId: batchGroupId,
            name: file.name,
            type: detectFileType(file),
            planType: recordPlanType,
            extractionMode,
            clientName,
            createdAt: new Date(),
            status: STATUS.QUEUED,
            parsed: null,
            errorMsg: null,
            validationError: null,
            source: "session",
            submittedBy: currentUserEmail ?? null,
            file,
        }));

        setItems((prev) => [
            ...newItems,
            ...(replaceIds.length > 0 ? prev.filter((i) => !replaceIds.includes(i.id)) : prev),
        ]);
        setExpandedClients((prev) => new Set([...prev, batchGroupId]));
        setMode("submissions");

        submitBillingRateAudit({
            action: "FILE_UPLOAD_STARTED",
            planType,
            fileCount: Array.from(files).length,
            fileNames: Array.from(files).map((f) => f.name),
            ...(submissionSk ? { submissionId: submissionSk } : {}),
        });

        newItems.forEach((item) => {
            updateItem(item.id, { status: STATUS.PROCESSING });
        });

        const extractionPromises = newItems.map((item) =>
            extractParsed(item.file, parseOptions)
                .then(({ parsed, s3Key }) => {
                    // Guard the plan-type toggle against the workbook's own "Sold Product" marker.
                    if (parsed.soldProduct && parsed.soldProduct !== recordPlanType) {
                        const validationError = createValidationError("SOLD_PRODUCT_MISMATCH", {
                            soldProductLabel: planTypeLabel(parsed.soldProduct),
                            selectedLabel: planTypeLabel(recordPlanType),
                        });
                        updateItem(item.id, {
                            status: STATUS.ERROR,
                            errorMsg: validationError.message,
                            validationError,
                            parsed: null,
                        });
                        setValidationAlert((prev) => {
                            const base = prev?.groupId === batchGroupId ? prev.messages : [];
                            return { groupId: batchGroupId, messages: [...base, `${item.name}: ${validationError.message}`] };
                        });
                        submitBillingRateAudit({
                            action: "EXTRACTION_FAILED",
                            planType: recordPlanType,
                            fileName: item.name,
                            errorMessage: validationError.message,
                            ...(submissionSk ? { submissionId: submissionSk } : {}),
                        });
                        return;
                    }
                    if (parsed.soldPlanCountMissing) {
                        setSoldPlanCountPrompt((current) => {
                            const entry = {
                                id: item.id,
                                file: item.file,
                                name: item.name,
                                parseOptions,
                                recordPlanType,
                                batchGroupId,
                                submissionSk,
                            };
                            if (!current) return { entries: [entry], value: "" };
                            if (current.entries.some((existing) => existing.id === item.id)) return current;
                            return { ...current, entries: [...current.entries, entry] };
                        });
                        return;
                    }
                    const resolvedClientName = parsed.group?.name ?? null;
                    updateItem(item.id, {
                        status: STATUS.DONE,
                        parsed,
                        s3Key,
                        ...(resolvedClientName && !item.clientName ? { clientName: resolvedClientName } : {}),
                    });
                    if (resolvedClientName) {
                        setItems((prev) =>
                            prev.map((i) =>
                                i.groupId === batchGroupId && !i.clientName
                                    ? { ...i, clientName: resolvedClientName }
                                    : i
                            )
                        );
                    }
                    submitBillingRateAudit({
                        action: "EXTRACTION_SUCCESS",
                        planType,
                        fileName: item.name,
                        s3Key,
                        clientName: resolvedClientName,
                        feesCount: parsed.fees?.length ?? 0,
                        plansCount: parsed.plans?.length ?? 0,
                        ...(submissionSk ? { submissionId: submissionSk } : {}),
                    });
                })
                .catch((err) => {
                    const validationError = toValidationError(err, { fileName: item.name });
                    updateItem(item.id, {
                        status: STATUS.ERROR,
                        errorMsg: err.message,
                        validationError,
                    });
                    setValidationAlert((prev) => {
                        const base = prev?.groupId === batchGroupId ? prev.messages : [];
                        return { groupId: batchGroupId, messages: [...base, `${item.name}: ${validationError.message}`] };
                    });
                    submitBillingRateAudit({
                        action: "EXTRACTION_FAILED",
                        planType,
                        fileName: item.name,
                        errorMessage: err.message,
                        ...(submissionSk ? { submissionId: submissionSk } : {}),
                    });
                })
        );

        return Promise.allSettled(extractionPromises);
    };

    // ─── ASO async upload flow ────────────────────────────────────────────────
    const handleAsoUpload = async (files, { clientName = "", groupId = null, replaceIds = [] } = {}) => {
        const batchGroupId = groupId ?? crypto.randomUUID();
        const fileList = Array.from(files);

        // When adding to an existing group, reuse the existing submission record and client name
        const existingGroupItems = groupId
            ? items.filter((i) => i.groupId === groupId && i.dbRecord?.sk)
            : [];
        const existingSk = existingGroupItems[0]?.dbRecord?.sk ?? null;
        // Reuse the existing SK as the S3 path prefix when appending so overwritten files
        // land on the same S3 key and the old object is replaced rather than orphaned.
        const submissionId = existingSk ?? crypto.randomUUID();
        const resolvedClientName = clientName || existingGroupItems[0]?.clientName || "";

        // IDs of existing items in the group that share a filename with the incoming files (will be overwritten)
        const incomingNames = new Set(fileList.map((f) => f.name));
        const overwrittenIds = groupId
            ? items.filter((i) => i.groupId === groupId && incomingNames.has(i.name)).map((i) => i.id)
            : [];

        const newItems = fileList.map((file) => ({
            id: crypto.randomUUID(),
            groupId: batchGroupId,
            name: file.name,
            type: detectFileType(file),
            planType: "aso",
            clientName: resolvedClientName,
            createdAt: new Date(),
            status: STATUS.PROCESSING,
            parsed: null,
            errorMsg: null,
            source: "session",
            submittedBy: currentUserEmail ?? null,
            file,
            isAsyncSubmission: true,
        }));

        const excludeIds = new Set([...replaceIds, ...overwrittenIds]);
        setItems((prev) => [
            ...newItems,
            ...(excludeIds.size > 0 ? prev.filter((i) => !excludeIds.has(i.id)) : prev),
        ]);
        setExpandedClients((prev) => new Set([...prev, batchGroupId]));
        setSubmissionStatusByGroupId((prev) => ({ ...prev, [batchGroupId]: "PENDING" }));
        setMode("submissions");

        if (existingSk) {
            toast.info(`Adding ${fileList.length} file${fileList.length === 1 ? "" : "s"} to "${resolvedClientName || "this submission"}"…`, {
                autoClose: 4000,
                className: "billing-rate-builder__toast--info",
            });
            setExtractedDataByGroupId((prev) => {
                const next = { ...prev };
                delete next[batchGroupId];
                delete next[existingSk];
                return next;
            });
        }

        try {
            const uploadedFiles = await Promise.all(
                fileList.map(async (file) => {
                    const fileType = detectAsoFileType(file);
                    const { url, s3Key } = await fetchBillingRatePresignedUrl({
                        fileType,
                        fileName: file.name,
                        submissionId,
                    });
                    await fetch(url, {
                        method: "PUT",
                        body: file,
                        headers: { "Content-Type": file.type || "application/octet-stream" },
                    });
                    return { fileType, fileName: file.name, s3Key, submittedBy: currentUserEmail ?? null };
                })
            );

            let sk;
            if (existingSk) {
                await appendFilesToBillingRateSubmission({ sk: existingSk, files: uploadedFiles });
                sk = existingSk;
            } else {
                const result = await createBillingRateSubmissionAsync({
                    submissionId,
                    clientName: resolvedClientName,
                    files: uploadedFiles,
                });
                sk = result.sk;
            }

            const dbRecord = { pk: "billing-rate-submission", sk };
            setItems((prev) =>
                prev.map((item) => {
                    if (newItems.some((ni) => ni.id === item.id)) {
                        return { ...item, dbRecord, groupId: sk };
                    }
                    if (item.groupId === batchGroupId || item.groupId === sk) {
                        return { ...item, dbRecord: item.dbRecord ?? dbRecord, groupId: sk };
                    }
                    return item;
                })
            );
            setExpandedClients((prev) => {
                const next = new Set(prev);
                if (next.has(batchGroupId)) { next.delete(batchGroupId); next.add(sk); }
                return next;
            });
            setSubmissionStatusByGroupId((prev) => {
                const next = { ...prev };
                delete next[batchGroupId];
                next[sk] = "PENDING";
                return next;
            });

            startPolling(sk, sk);
            submitBillingRateAudit({
                action: "FILE_UPLOAD_STARTED",
                planType,
                fileCount: fileList.length,
                fileNames: fileList.map((f) => f.name),
                submissionId: sk,
                clientName: resolvedClientName,
            });
            if (existingSk) {
                clearDrafts([sk, batchGroupId]);
                if (getDraftsMeta) setDraftsMeta(getDraftsMeta());
            }
        } catch (err) {
            newItems.forEach((item) => {
                updateItem(item.id, { status: STATUS.ERROR, errorMsg: err.message });
            });
            setSubmissionStatusByGroupId((prev) => {
                const next = { ...prev };
                delete next[batchGroupId];
                return next;
            });
        }
    };

    const handleXlsxChange = (e) => {
        if (e.target.files?.length) {
            setFilePickerLoading("xlsx");
            handleFiles(e.target.files, { clientName: levelFundedClientName, replaceIds: pendingRemoveIds })
                .finally(() => setFilePickerLoading(null));
            if (pendingRemoveIds.length > 0) setPendingRemoveIds([]);
        }
        e.target.value = "";
    };

    const handleAsoXlsxChange = (e) => {
        if (e.target.files?.length) {
            setFilePickerLoading("aso-xlsx");
            handleFiles(e.target.files, {
                recordPlanType: "aso",
                extractionMode: "spreadsheet",
                parseOptions: { planNameFallback: true },
            })
                .finally(() => setFilePickerLoading(null));
        }
        e.target.value = "";
    };

    const handlePdfChange = (e) => {
        if (e.target.files?.length) {
            setFilePickerLoading("aso");
            handleAsoUpload(e.target.files, {
                clientName: pendingAddClientName ?? asoClientName,
                groupId: pendingGroupId ?? undefined,
                replaceIds: pendingRemoveIds,
            })
                .finally(() => {
                    setFilePickerLoading(null);
                    setPendingAddClientName(null);
                });
            if (pendingRemoveIds.length > 0) setPendingRemoveIds([]);
            setPendingGroupId(null);
        }
        e.target.value = "";
    };

    const handleProcessClick = (groupId, clientItems, draftId, { allowStartFresh = false } = {}) => {
        if (draftId) {
            const isJsonImport = clientItems.some((i) => i.type === "json");
            setDraftCheckPending({
                groupId,
                clientItems,
                draftId,
                isJsonImport,
                allowStartFresh: allowStartFresh && !isJsonImport,
            });
        } else {
            handleProceed(groupId, clientItems);
        }
    };

    const handleProceed = async (groupId, clientItems) => {
        const clientName = clientItems[0]?.clientName ?? groupId;
        const itemPlanType = clientItems[0]?.planType ?? planType;
        // ASO Sold Rates workbooks are extracted locally and reuse the level-funded
        // pipeline end-to-end; only the persisted record stays tagged "aso" so the
        // submission still lists and files under ASO.
        const isAsoSpreadsheet = itemPlanType === "aso"
            && clientItems.length > 0
            && clientItems.every((i) => i.extractionMode === "spreadsheet" && i.parsed != null);
        const pipelinePlanType = isAsoSpreadsheet ? "level-funded" : itemPlanType;
        if (proceedingClient) return;
        setProceedingClient(groupId);
        setValidationAlert(null);
        try {
            const asyncExtracted = extractedDataByGroupId[groupId];
            const parsedArray = clientItems.map((i) => i.parsed).filter(Boolean);
            let merged = asyncExtracted ?? mergeParsedResults(parsedArray, { planType: pipelinePlanType });
            merged = promoteRenewalDatesToGroup(merged);
            if (!merged) throw new Error("No parsed data available for this client.");
            if (merged.fees) {
                merged.fees = normalizeFees(merged.fees, { planType: pipelinePlanType });
                merged.fees = filterAsaNetworkAccessFees(merged.fees, merged);
            }

            const forceShared = clientItems.some((i) => isStopLossFile(i.name));

            if (pipelinePlanType === "aso" && !asoClientName && !clientItems[0]?.clientName && !forceShared) {
                throw new Error("Client name is required for ASO. Search for a client before proceeding.");
            }

            let resolvedAccountRow = null;
            let accountBenefitPlans = [];

            if ((pipelinePlanType === "aso" && (asoClientName || clientItems[0]?.clientName)) || forceShared) {
                resolvedAccountRow = asoSelectedAccount ?? null;

                if (!resolvedAccountRow && asoClientName) {
                    const r = await fetchAccounts({ accountName: asoClientName, limit: 1 }).catch(() => null);
                    resolvedAccountRow = r?.rows?.[0] ?? null;
                }
                if (!resolvedAccountRow) {
                    const storedHccId = clientItems.find((i) => i.resolvedAccountHccId)?.resolvedAccountHccId;
                    if (storedHccId) {
                        const r = await fetchAccounts({ accountHccId: storedHccId, limit: 1 }).catch(() => null);
                        resolvedAccountRow = r?.rows?.[0] ?? null;
                    }
                }
                if (!resolvedAccountRow && merged.group?.code) {
                    const r = await fetchAccounts({ accountHccId: merged.group.code, limit: 1 }).catch(() => null);
                    resolvedAccountRow = r?.rows?.[0] ?? null;
                }
                if (!resolvedAccountRow) {
                    const nameCandidate = clientItems.find((i) => i.clientName)?.clientName;
                    if (nameCandidate) {
                        const r = await fetchAccounts({ accountName: nameCandidate, limit: 1 }).catch(() => null);
                        resolvedAccountRow = r?.rows?.[0] ?? null;
                    }
                }

                const accountKey = resolvedAccountRow?.accountKey ?? null;
                if (resolvedAccountRow?.accountHccId) {
                    merged.group = { ...merged.group, code: resolvedAccountRow.accountHccId };
                }
                const benefitPlans = accountKey
                    ? await fetchBenefitPlansByAccountKey({ accountKey }).catch(() => [])
                    : [];
                const byHccId = new Map();
                (benefitPlans ?? [])
                    .filter((p) => p.benefitPlanHccId && p.benefitPlanStatus === 'a')
                    .forEach((p) => {
                        const existing = byHccId.get(p.benefitPlanHccId);
                        const version = p.versionEffDateKey ?? Number.NEGATIVE_INFINITY;
                        const existingVersion = existing?.versionEffDateKey ?? Number.NEGATIVE_INFINITY;
                        if (!existing || version > existingVersion) byHccId.set(p.benefitPlanHccId, p);
                    });
                accountBenefitPlans = [...byHccId.values()];
                merged.plans = accountBenefitPlans.map((p, i) => ({
                    index: i + 1,
                    name: p.benefitPlanHccId ?? null,
                    planName: p.benefitPlanName ?? null,
                    planId: p.benefitPlanHccId ?? null,
                    planNameSource: p.benefitPlanHccId ? "planId" : null,
                    benefitPlanCode: null,
                    billPlanCode: null,
                    billingRateTotals: { eeOnly: null, eesEePlus1: null, eec: null, famEePlus2: null },
                }));
            }

            const needsCategories = pipelinePlanType === "aso" || forceShared;
            const tierOverrides = coverageTierSelections[groupId] ?? {};

            if (pipelinePlanType === "level-funded") {
                const accountOverride = accountSelections[groupId] ?? null;
                const planMap = planSelections[groupId] ?? {};

                if (accountOverride?.accountHccId) {
                    merged.group = { ...merged.group, code: accountOverride.accountHccId };
                }
                if (Object.keys(planMap).length > 0) {
                    merged.plans = (merged.plans ?? []).map((plan) => {
                        const selection = planMap[plan.index];
                        // Carry the chosen HCC ID into planId so coverage-tier resolution and
                        // shared-component grouping key off the correct benefit plan.
                        return selection
                            ? {
                                ...plan,
                                planId: selection.benefitPlanHccId,
                                name: selection.benefitPlanHccId,
                                planName: selection.planName,
                            }
                            : plan;
                    });
                }

                const apCheck = await validateBillingRateAccountAndPlans(merged, {
                    fetchAccounts,
                    fetchBenefitPlansByAccountKey,
                    accountOverride,
                });
                accountBenefitPlans = apCheck.accountBenefitPlans ?? [];
                const apValidation = createValidationCollector().addAll(apCheck.errors);

                if (apValidation.hasHardStop()) {
                    const apHardStops = apValidation.hardStops();
                    const accountStop = apHardStops.find((e) => ACCOUNT_HARD_STOP_SET.has(e.code));
                    if (accountStop) {
                        // Reject the invalid account selection so it must be re-chosen.
                        setAccountSelections((prev) => {
                            if (!prev[groupId]) return prev;
                            const next = { ...prev };
                            delete next[groupId];
                            return next;
                        });
                        const accountSearchSeed = merged.group?.name ?? clientName ?? "";
                        seedAccountSearch(accountSearchSeed);
                        setAccountResolution({
                            groupId,
                            accountHccId: accountStop.accountHccId ?? merged.group?.code ?? merged.group?.name ?? null,
                            message: accountStop.message,
                        });
                        setPlanResolution(null);
                        setCoverageTierResolution(null);
                        setValidationAlert({ groupId, messages: [accountStop.message] });
                        setMode("submissions");
                        return;
                    }

                    const unresolvedPlans = unresolvedBenefitPlanHardStops(apHardStops, planMap);
                    if (unresolvedPlans.length > 0) {
                        setAccountResolution(null);
                        setPlanResolution({
                            groupId,
                            plans: benefitPlanResolutionPlans(unresolvedPlans),
                            options: apCheck.accountBenefitPlans,
                        });
                        setCoverageTierResolution(null);
                        setValidationAlert({
                            groupId,
                            messages: unresolvedPlans.map((e) => e.message),
                        });
                        return;
                    }

                    const fetchFailed = apHardStops.find((e) => e.code === "BENEFIT_PLANS_FETCH_FAILED");
                    if (fetchFailed) {
                        setAccountResolution(null);
                        setPlanResolution(null);
                        setCoverageTierResolution(null);
                        setValidationAlert({ groupId, messages: [fetchFailed.message] });
                        return;
                    }
                }

                setAccountResolution(null);
                setPlanResolution(null);

                if (apCheck.account?.accountHccId) {
                    merged.group = { ...merged.group, code: apCheck.account.accountHccId };
                }
                if (apCheck.account) {
                    resolvedAccountRow = apCheck.account;
                }
            }

            // Renewal fees are merged in applyDocumentReconciliation; exclude from initial mapping.
            const allFees = merged.fees ?? [];
            const renewalFees = filterBillableRenewalFees(allFees);
            const primaryFees = dedupePrimaryFees(filterBillablePrimaryFees(allFees), { planType: pipelinePlanType });
            const submissionSk = clientItems.find((i) => i.dbRecord?.sk)?.dbRecord?.sk ?? null;
            const asOfDate = await resolveBillingRateAsOfDate({
                group: merged.group,
                parsed: merged,
                submissionSk,
                fetchSubmission: fetchBillingRateSubmission,
            });
            merged.group = {
                ...merged.group,
                effectiveDate: merged.group?.effectiveDate ?? asOfDate,
                billingPeriodStart: merged.group?.billingPeriodStart ?? asOfDate,
            };
            const mergedForComponents = {
                ...merged,
                fees: primaryFees,
            };

            const [uploadContext, billingRateCategories, billingRateSubCategories] = await Promise.all([
                fetchBillingRateUploadContext(mergedForComponents, {
                    fetchCoverageTierSetByBenefitPlan,
                    coverageTierSetOverrides: tierOverrides,
                }),
                fetchBillingRateCategories().catch(() => null),
                fetchBillingRateSubCategories().catch(() => null),
            ]);
            const result = mapToBillingComponents(mergedForComponents, { uploadContext, billingRateCategories, planType: pipelinePlanType, forceShared });

            applyDocumentReconciliation({
                componentPayloads: result.billingRateComponentPayloads,
                renewalFees,
                accountHccId: resolvedAccountRow?.accountHccId ?? result.groupId,
                groupId: result.groupId,
                plans: merged.plans ?? [],
                effectiveDate: asOfDate,
                // Sold-rate-only LF uploads map one fee row per component; skip fuzzy dedupe on Proceed.
                skipFuzzyDedupe: pipelinePlanType === "level-funded" && renewalFees.length === 0,
            });

            result.billingRatePayloads = syncBillingRatePayloadsFromComponents(
                result.billingRatePayloads,
                result.billingRateComponentPayloads,
                {
                    plans: merged.plans ?? [],
                    groupId: result.groupId,
                    groupName: merged.group?.name ?? clientName,
                    effectiveDate: asOfDate,
                    fees: allFees,
                    billingCategoryRows: billingRateCategories,
                },
            );

            // Resolve reference values onto the payloads so downstream validation
            // and editing have the codeEntry (sub category) and shortName (billing
            // category) without re-fetching in steps two/three.
            applyFetchedCategoryLookups(
                result.billingRateComponentPayloads,
                result.billingRatePayloads,
                { subCategories: billingRateSubCategories, billingCategories: billingRateCategories },
            );

            // Check each generated component against existing HRP records so step
            // two can warn about name/ID collisions that will be amended on submit.
            const collisionCandidates = result.billingRateComponentPayloads.map((wrapper) => {
                const component = wrapper?.billingRateComponentPayload?.billingRateComponent;
                return {
                    componentName: component?.componentName ?? "",
                    componentId: component?.definition?.rateClassifications?.componentId ?? "",
                };
            });
            const componentCollisions = collisionCandidates.length > 0
                ? await checkBillingRateComponentCollisions(collisionCandidates).catch(() => null)
                : null;

            const rateCollisions = await fetchRateCollisions(
                accountBenefitPlans,
                result.billingRatePayloads,
                fetchBillingRateByBenefitPlanKey,
            );

            const hrpAsOfDate = extractBillingRateAsOfDate({
                componentPayloads: result.billingRateComponentPayloads,
                ratePayloads: result.billingRatePayloads,
                group: merged.group,
                parsed: merged,
                asOfDateHint: asOfDate,
            }) ?? asOfDate;

            stampAsOfDateOnComponentPayloads(result.billingRateComponentPayloads, hrpAsOfDate);

            let hrpSnapshot = { components: [], plans: {}, asOfDate: hrpAsOfDate };
            let hrpReconciliation = [];
            let hasHrpReconciliation = false;
            let hrpContext = { groupId: result.groupId };
            if (resolvedAccountRow?.accountKey) {
                hrpContext = await prepareHrpReconciliationContext({
                    componentPayloads: result.billingRateComponentPayloads,
                    fees: allFees,
                    ratePayloads: result.billingRatePayloads,
                    planType: pipelinePlanType,
                    accountHccId: resolvedAccountRow.accountHccId ?? result.groupId,
                    accountOverride: resolvedAccountRow,
                    clientName,
                    asOfDate: hrpAsOfDate,
                    group: merged.group,
                    parsed: merged,
                    globalCollisionRows: Array.isArray(componentCollisions?.rows)
                        ? componentCollisions.rows
                        : [],
                    fetchAccounts,
                    fetchBenefitPlansByAccountKey,
                    fetchBillingRateComponentsByAccountKey,
                    fetchBillingRateAndComponents,
                });
                ({ hrpSnapshot, hrpReconciliation, hasHrpReconciliation } = hrpContext);
            }

            if (!hasHrpReconciliation) {
                applyComponentCollisionAmendments(
                    result.billingRateComponentPayloads,
                    Array.isArray(componentCollisions?.rows) ? componentCollisions.rows : [],
                    result.groupId,
                );
            }
            applyRateCollisionAmendments(
                result.billingRatePayloads,
                Array.isArray(rateCollisions) ? rateCollisions : [],
            );

            const validation = createValidationCollector()
                .addAll(clientItems.map((i) => i.validationError).filter(Boolean))
                .addAll(result.errors)
                .add(result.billingRateComponentPayloads.length === 0
                    ? createValidationError("NO_COMPONENTS_GENERATED")
                    : null);

            // TODO: Remove this logging
            console.groupCollapsed(`[Billing Rate Builder] Proceed: ${clientName}`);
            console.log("Files", clientItems.map((i) => i.name));
            console.log("Plan type", itemPlanType, { forceShared });
            console.log("Parsed workbook\n", JSON.stringify(merged, null, 2));
            console.log("Upload context\n", JSON.stringify(uploadContext, null, 2));
            console.log("Component payloads\n", JSON.stringify(result.billingRateComponentPayloads, null, 2));
            console.log("Billing rate payloads\n", JSON.stringify(result.billingRatePayloads, null, 2));
            console.log("Validation errors\n", JSON.stringify(validation.all(), null, 2));
            console.groupEnd();

            // Coverage tier hard stops block both plan types and show a per-plan selector: the
            // tier set is only knowable from HRP, so guessing it silently misconfigures the rate.
            // Remaining hard stops still block level-funded only.
            if (validation.hasHardStop()) {
                const hardStops = validation.hardStops();
                const unresolvedCoverage = unresolvedCoverageTierHardStops(hardStops, tierOverrides);
                if (unresolvedCoverage.length > 0) {
                    setCoverageTierResolution({
                        groupId,
                        plans: coverageTierResolutionPlans(unresolvedCoverage),
                    });
                    setValidationAlert({
                        groupId,
                        messages: unresolvedCoverage.map((error) => error.message),
                    });
                    return;
                }
                if (pipelinePlanType === "level-funded") {
                    setCoverageTierResolution(null);
                    setValidationAlert({ groupId, messages: validation.messages(VALIDATION_SEVERITY.HARD_STOP) });
                    return;
                }
            }

            setCoverageTierResolution(null);

            const existingDbRecord = clientItems.find((i) => i.dbRecord?.sk)?.dbRecord ?? null;
            const dbRecord = existingDbRecord ?? await createBillingRateSubmission({
                clientName,
                fileName: clientItems.map((i) => i.name).join(", "),
                fileType: clientItems[0].type,
                planType: itemPlanType,
                componentCount: result.billingRateComponentPayloads.length,
                rateCount: result.billingRatePayloads.length,
                s3Keys: clientItems.map((i) => i.s3Key).filter(Boolean),
            }).catch(() => null);

            const stableGroupId = dbRecord?.sk ?? existingDbRecord?.sk ?? groupId;
            const resolvedAccountHccId = resolvedAccountRow?.accountHccId ?? null;
            setItems((prev) => prev.map((item) => {
                if (!clientItems.some((ci) => ci.id === item.id)) return item;
                const updates = {};
                const recordSk = dbRecord?.sk ?? existingDbRecord?.sk ?? null;
                if (recordSk) {
                    updates.dbRecord = {
                        pk: dbRecord?.pk ?? existingDbRecord?.pk ?? "billing-rate-submission",
                        sk: recordSk,
                    };
                    // Align session groupId with the DB sk (same as ASO async upload) so draft
                    // keys, table rows, and history refresh all use one stable identifier.
                    if (item.groupId !== recordSk) updates.groupId = recordSk;
                }
                if (resolvedAccountHccId) updates.resolvedAccountHccId = resolvedAccountHccId;
                return Object.keys(updates).length ? { ...item, ...updates } : item;
            }));
            if (stableGroupId !== groupId) {
                setExpandedClients((prev) => {
                    const next = new Set(prev);
                    if (next.has(groupId)) {
                        next.delete(groupId);
                        next.add(stableGroupId);
                    }
                    return next;
                });
            }

            submitBillingRateAudit({
                action: "PROCEED_CLICKED",
                planType: itemPlanType,
                clientName,
                submissionId: dbRecord?.sk ?? null,
                componentCount: result.billingRateComponentPayloads.length,
                rateCount: result.billingRatePayloads.length,
            });

            setMergedClients((prev) => new Set([...prev, stableGroupId]));
            onComponentPayloadsExtracted?.(result.billingRateComponentPayloads);
            onRatePayloadsExtracted?.(result.billingRatePayloads);
            onFeesExtracted?.(allFees);
            onPlansExtracted?.(Array.isArray(merged.plans) ? merged.plans : []);
            onUploadContextExtracted?.(uploadContext ?? null);
            onSubCategoryRowsExtracted?.(Array.isArray(billingRateSubCategories) ? billingRateSubCategories : []);
            onBillingCategoryRowsExtracted?.(Array.isArray(billingRateCategories) ? billingRateCategories : []);
            onComponentCollisionsExtracted?.(Array.isArray(componentCollisions?.rows) ? componentCollisions.rows : []);
            onRateCollisionsExtracted?.(Array.isArray(rateCollisions) ? rateCollisions : []);
            onHrpSnapshotExtracted?.(hrpSnapshot);
            onHrpReconciliationExtracted?.(hrpReconciliation);
            onGroupIdExtracted?.(hrpContext.groupId ?? result.groupId);
            onPlanTypeExtracted?.(pipelinePlanType);
            onProceed?.(dbRecord ? { ...dbRecord, clientName, tableGroupId: dbRecord.sk ?? groupId } : { tableGroupId: groupId });
        } catch (err) {
            console.error("[Billing Rate Builder] Failed to proceed", err);
            setValidationAlert({ groupId, messages: [err?.message ?? String(err)] });
        } finally {
            setProceedingClient(null);
        }
    };

    const handleAccountSearchChange = (e) => {
        const value = e.target.value;
        setAccountSearchTerm(value);
        clearTimeout(accountSearchDebounceRef.current);
        if (!value.trim()) {
            setAccountSearchItems([]);
            setAccountSearchOpen(false);
            return;
        }
        accountSearchDebounceRef.current = setTimeout(() => {
            setAccountSearchLoading(true);
            fetchAccounts({ accountName: value, limit: 10 })
                .then((result) => {
                    setAccountSearchItems(result?.rows ?? []);
                    setAccountSearchOpen(true);
                })
                .catch(() => setAccountSearchItems([]))
                .finally(() => setAccountSearchLoading(false));
        }, 300);
    };

    const seedAccountSearch = (searchTerm) => {
        const candidates = buildAccountNameSearchCandidates(searchTerm);
        const terms = candidates.length > 0 ? candidates : [String(searchTerm ?? "").trim()].filter(Boolean);
        if (terms.length === 0) {
            setAccountSearchTerm("");
            setAccountSearchItems([]);
            setAccountSearchOpen(false);
            return;
        }

        setAccountSearchTerm(terms[0]);
        setAccountSearchLoading(true);

        const runSearch = async (index = 0) => {
            const term = terms[index];
            if (!term) {
                setAccountSearchItems([]);
                setAccountSearchOpen(false);
                setAccountSearchLoading(false);
                return;
            }
            try {
                const result = await fetchAccounts({ accountName: term, limit: 10 });
                const rows = result?.rows ?? [];
                if (rows.length > 0 || index >= terms.length - 1) {
                    setAccountSearchItems(rows);
                    setAccountSearchOpen(rows.length > 0);
                    setAccountSearchLoading(false);
                    return;
                }
            } catch {
                if (index >= terms.length - 1) {
                    setAccountSearchItems([]);
                    setAccountSearchOpen(false);
                    setAccountSearchLoading(false);
                    return;
                }
            }
            await runSearch(index + 1);
        };

        void runSearch();
    };

    const handleAccountSearchSelect = (groupId, row) => {
        setAccountSelections((prev) => ({
            ...prev,
            [groupId]: {
                accountHccId: row.accountHccId,
                accountKey: row.accountKey,
                accountName: row.accountName,
            },
        }));
        setAccountSearchTerm(row.accountName ?? row.accountHccId ?? "");
        setAccountSearchOpen(false);
        setAccountSearchItems([]);
    };

    const handlePlanSelect = (groupId, planIndex, option) => {
        setPlanSelections((prev) => ({
            ...prev,
            [groupId]: {
                ...(prev[groupId] ?? {}),
                [planIndex]: option
                    ? { planName: option.benefitPlanName, benefitPlanHccId: option.benefitPlanHccId }
                    : null,
            },
        }));
    };

    const handleAccountContinue = (groupId) => {
        const clientItems = items.filter((i) => i.groupId === groupId);
        handleProceed(groupId, clientItems);
    };

    const handlePlanContinue = (groupId) => {
        const clientItems = items.filter((i) => i.groupId === groupId);
        handleProceed(groupId, clientItems);
    };

    const handleCoverageTierSelect = (groupId, benefitPlanHccId, coverageTierSetName) => {
        setCoverageTierSelections((prev) => ({
            ...prev,
            [groupId]: {
                ...(prev[groupId] ?? {}),
                [benefitPlanHccId]: coverageTierSetName,
            },
        }));
    };

    const handleCoverageTierContinue = (groupId, clientItems) => {
        handleProceed(groupId, clientItems);
    };

    const toggleClient = (groupId) => {
        setExpandedClients((prev) => {
            const next = new Set(prev);
            next.has(groupId) ? next.delete(groupId) : next.add(groupId);
            return next;
        });
    };

    const handleReprocessGroupAsync = async (clientItems) => {
        const historyItems = clientItems.filter((i) => i.source === "history");
        const s3Keys = historyItems.flatMap((i) => i.s3Keys ?? []);
        if (s3Keys.length === 0) {
            const first = historyItems[0];
            if (first) {
                setPendingRemoveIds(historyItems.map((i) => i.id));
                handleReImport(first.groupId ?? first.id, first.clientName, first.type ?? FILE_TYPE.PDF);
            }
            return;
        }
        submitBillingRateAudit({
            action: "REPROCESS_CLICKED",
            submissionId: historyItems[0]?.dbRecord?.sk ?? null,
            clientName: historyItems[0]?.clientName ?? null,
            s3Keys,
        });
        historyItems.forEach((item) => {
            updateItem(item.id, { status: STATUS.PROCESSING, errorMsg: null, parsed: null });
        });
        const groupId = historyItems[0]?.groupId;
        setSubmissionStatusByGroupId((prev) => ({ ...prev, [groupId]: "PENDING" }));
        try {
            const fileNames = historyItems.map((i) => i.name);
            const files = s3Keys.map((s3Key, i) => ({
                fileType: detectAsoFileTypeFromName(fileNames[i] ?? s3Key),
                fileName: fileNames[i] ?? s3Key.split("/").pop(),
                s3Key,
                processingStatus: "PENDING",
                error: null,
                extractedData: null,
            }));
            const resolvedClientName = historyItems[0].clientName ?? asoClientName ?? null;
            const { sk } = await createBillingRateSubmissionAsync({
                submissionId: crypto.randomUUID(),
                clientName: resolvedClientName,
                files,
            });
            setItems((prev) =>
                prev.map((item) =>
                    historyItems.some((h) => h.id === item.id)
                        ? {
                            ...item,
                            isAsyncSubmission: true,
                            dbRecord: { pk: "billing-rate-submission", sk },
                            clientName: item.clientName ?? resolvedClientName,
                          }
                        : item
                )
            );
            startPolling(groupId, sk);
        } catch (err) {
            historyItems.forEach((item) => {
                updateItem(item.id, { status: STATUS.ERROR, errorMsg: err.message });
            });
            setSubmissionStatusByGroupId((prev) => {
                const next = { ...prev };
                delete next[groupId];
                return next;
            });
        }
    };

    const handleReprocess = async (item) => {
        submitBillingRateAudit({
            action: "REPROCESS_CLICKED",
            submissionId: item.dbRecord?.sk ?? null,
            clientName: item.clientName ?? null,
            s3Keys: item.s3Keys ?? [],
        });
        setValidationAlert(null);
        updateItem(item.id, { status: STATUS.PROCESSING, errorMsg: null, validationError: null, parsed: null });
        if (item.source === "session") {
            const parseOptions = item.planType === "aso" ? { planNameFallback: true } : {};
            extractParsed(item.file, parseOptions)
                .then(({ parsed, s3Key }) => {
                    if (parsed.soldPlanCountMissing) {
                        setSoldPlanCountPrompt((current) => {
                            const entry = {
                                id: item.id,
                                file: item.file,
                                name: item.name,
                                parseOptions,
                                recordPlanType: item.planType,
                                batchGroupId: item.groupId,
                                submissionSk: item.dbRecord?.sk ?? null,
                            };
                            if (!current) return { entries: [entry], value: "" };
                            if (current.entries.some((existing) => existing.id === item.id)) return current;
                            return { ...current, entries: [...current.entries, entry] };
                        });
                        return;
                    }
                    updateItem(item.id, { status: STATUS.DONE, parsed, s3Key });
                })
                .catch((err) => {
                    const validationError = toValidationError(err, { fileName: item.name });
                    updateItem(item.id, {
                        status: STATUS.ERROR,
                        errorMsg: err.message,
                        validationError,
                    });
                    setValidationAlert({ groupId: item.groupId, messages: [`${item.name}: ${validationError.message}`] });
                });
            return;
        }
        const s3Keys = item.s3Keys ?? [];
        if (s3Keys.length === 0) {
            updateItem(item.id, { status: STATUS.ERROR, errorMsg: "No S3 keys available for reprocessing." });
            return;
        }
        const groupId = item.groupId;
        setSubmissionStatusByGroupId((prev) => ({ ...prev, [groupId]: "PENDING" }));
        try {
            const files = s3Keys.map((s3Key) => ({
                fileType: detectAsoFileTypeFromName(item.name ?? s3Key),
                fileName: item.name ?? s3Key.split("/").pop(),
                s3Key,
                processingStatus: "PENDING",
                error: null,
                extractedData: null,
            }));
            const { sk } = await createBillingRateSubmissionAsync({
                submissionId: crypto.randomUUID(),
                clientName: item.clientName,
                files,
            });
            updateItem(item.id, { isAsyncSubmission: true, dbRecord: { pk: "billing-rate-submission", sk } });
            startPolling(groupId, sk);
        } catch (err) {
            updateItem(item.id, { status: STATUS.ERROR, errorMsg: err.message });
            setSubmissionStatusByGroupId((prev) => {
                const next = { ...prev };
                delete next[groupId];
                return next;
            });
        }
    };

    const handleReImport = (groupId, clientName, fileType) => {
        if (fileType === FILE_TYPE.PDF) {
            setPlanType("aso");
            setPendingGroupId(groupId);
            setPendingAddClientName(clientName === "—" ? "" : (clientName ?? ""));
            setPendingPicker("pdf");
        } else {
            setLevelFundedClientName(clientName === "—" ? "" : (clientName ?? ""));
            setPlanType("level-funded");
            setPendingPicker("xlsx");
        }
    };

    const handleRemoveFile = (groupId, item, clientItems) => {
        if (clientItems.length <= 1) {
            toast.error("Cannot remove the last file. Delete the submission instead.", {
                className: "billing-rate-builder__toast--error",
            });
            return;
        }
        setFileDeleteConfirm({ groupId, item, clientItems, displayName: item.name });
    };

    const confirmRemoveFile = async () => {
        const { groupId, item, clientItems } = fileDeleteConfirm;
        const sk = item.dbRecord?.sk ?? getSubmissionSk(clientItems);
        setFileDeleteLoading(true);
        submitBillingRateAudit({
            action: "FILE_DELETE_CLICKED",
            submissionId: sk,
            clientName: item.clientName ?? null,
            fileName: item.name,
        });
        try {
            if (sk) {
                const result = await removeFilesFromBillingRateSubmission({ sk, fileNames: [item.name] });
                setItems((prev) => prev.filter((i) => i.id !== item.id));
                clearDrafts([groupId, sk]);
                if (getDraftsMeta) setDraftsMeta(getDraftsMeta());
                syncSubmissionAfterFileChange(groupId, sk, result);
            } else {
                setItems((prev) => prev.filter((i) => i.id !== item.id));
            }
            setFileDeleteConfirm(null);
        } catch {
            window.alert(`Failed to remove "${item.name}". Please try again.`);
        } finally {
            setFileDeleteLoading(false);
        }
    };

    const handleDelete = (groupId, clientItems) => {
        const displayName = clientItems[0]?.clientName || groupId.slice(0, 8);
        setDeleteConfirm({ groupId, clientItems, displayName });
    };

    const confirmDelete = async () => {
        const { groupId, clientItems, displayName } = deleteConfirm;
        setDeleteLoading(true);
        submitBillingRateAudit({
            action: "DELETE_CLICKED",
            submissionId: clientItems[0]?.dbRecord?.sk ?? null,
            clientName: clientItems[0]?.clientName ?? null,
        });
        const sksToDelete = getDeletableSubmissionSks(clientItems);
        try {
            await Promise.all(sksToDelete.map((sk) => deleteBillingRateSubmission(sk)));
            setItems((prev) => prev.filter((i) => !clientItems.some((c) => c.id === i.id)));
            clearDrafts([
                groupId,
                ...clientItems.map((i) => i.dbRecord?.sk),
            ]);
            if (getDraftsMeta) setDraftsMeta(getDraftsMeta());
            setDeleteConfirm(null);
        } catch {
            window.alert(`Failed to delete submission for "${displayName}". Please try again.`);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleManualRefreshGroup = (groupId, sk) => {
        pollSubmission(groupId, sk);
    };

    const advanceSoldPlanCountPrompt = () => {
        setSoldPlanCountPrompt((current) => {
            const entries = current?.entries?.slice(1) ?? [];
            return entries.length > 0 ? { entries, value: "" } : null;
        });
    };

    const handleSoldPlanCountContinue = () => {
        const entry = soldPlanCountPrompt?.entries?.[0];
        const count = Number(soldPlanCountPrompt?.value);
        if (!entry || !Number.isInteger(count) || count < 1 || count > MAX_SOLD_PLAN_COUNT) {
            setSoldPlanCountPrompt((current) => current && ({
                ...current,
                error: `Enter a whole number from 1 to ${MAX_SOLD_PLAN_COUNT}.`,
            }));
            return;
        }
        setSoldPlanCountPrompt((current) => current && ({ ...current, submitting: true, error: null }));
        extractParsed(entry.file, { ...entry.parseOptions, soldPlanCount: count })
            .then(({ parsed, s3Key }) => {
                if (parsed.soldProduct && parsed.soldProduct !== entry.recordPlanType) {
                    const validationError = createValidationError("SOLD_PRODUCT_MISMATCH", {
                        soldProductLabel: planTypeLabel(parsed.soldProduct),
                        selectedLabel: planTypeLabel(entry.recordPlanType),
                    });
                    updateItem(entry.id, {
                        status: STATUS.ERROR,
                        errorMsg: validationError.message,
                        validationError,
                        parsed: null,
                    });
                    setValidationAlert({ groupId: entry.batchGroupId, messages: [`${entry.name}: ${validationError.message}`] });
                } else {
                    const resolvedClientName = parsed.group?.name ?? null;
                    updateItem(entry.id, {
                        status: STATUS.DONE,
                        parsed,
                        s3Key,
                        ...(resolvedClientName ? { clientName: resolvedClientName } : {}),
                    });
                    if (resolvedClientName) {
                        setItems((prev) => prev.map((item) => (
                            item.groupId === entry.batchGroupId && !item.clientName
                                ? { ...item, clientName: resolvedClientName }
                                : item
                        )));
                    }
                    submitBillingRateAudit({
                        action: "EXTRACTION_SUCCESS",
                        planType: entry.recordPlanType,
                        fileName: entry.name,
                        s3Key,
                        clientName: resolvedClientName,
                        feesCount: parsed.fees?.length ?? 0,
                        plansCount: parsed.plans?.length ?? 0,
                        ...(entry.submissionSk ? { submissionId: entry.submissionSk } : {}),
                    });
                }
                advanceSoldPlanCountPrompt();
            })
            .catch((err) => {
                const validationError = toValidationError(err, { fileName: entry.name });
                updateItem(entry.id, {
                    status: STATUS.ERROR,
                    errorMsg: err.message,
                    validationError,
                });
                setValidationAlert({ groupId: entry.batchGroupId, messages: [`${entry.name}: ${validationError.message}`] });
                advanceSoldPlanCountPrompt();
            });
    };

    const grouped = groupItemsByClient(items);
    const filteredGrouped = tableFilter === "all"
        ? grouped
        : new Map(Array.from(grouped.entries()).filter(([, clientItems]) => clientItems[0]?.planType === tableFilter));

    const isGroupSubmitted = (gId, clientItems) =>
        submittedGroupIds.has(gId) || clientItems.every((i) => i.status === STATUS.SUBMITTED);

    const PAGE_SIZE = 10;
    const inProgressEntries = Array.from(filteredGrouped.entries()).filter(([gId, ci]) => !isGroupSubmitted(gId, ci));
    const completedEntries = Array.from(filteredGrouped.entries()).filter(([gId, ci]) => isGroupSubmitted(gId, ci));
    const inProgressPageCount = Math.max(1, Math.ceil(inProgressEntries.length / PAGE_SIZE));
    const completedPageCount = Math.max(1, Math.ceil(completedEntries.length / PAGE_SIZE));
    const inProgressPageEntries = inProgressEntries.slice((inProgressPage - 1) * PAGE_SIZE, inProgressPage * PAGE_SIZE);
    const completedPageEntries = completedEntries.slice((completedPage - 1) * PAGE_SIZE, completedPage * PAGE_SIZE);

    // Hard-stop validation surfacing. Set when a file fails to parse/upload or a
    // proceed-time hard stop fires; cleared whenever a new file is uploaded.
    const alertMessages = validationAlert?.messages ?? [];
    const activeAccountResolution = accountResolution;
    const activePlanResolution = planResolution;
    const activeCoverageResolution = coverageTierResolution;
    const resolutionSelections = activeCoverageResolution
        ? (coverageTierSelections[activeCoverageResolution.groupId] ?? {})
        : {};
    const allCoveragePlansSelected = activeCoverageResolution?.plans?.length > 0
        && activeCoverageResolution.plans.every((plan) => resolutionSelections[plan.benefitPlanHccId]);
    const accountSelected = activeAccountResolution
        ? Boolean(accountSelections[activeAccountResolution.groupId])
        : false;
    const planRemapSelections = activePlanResolution
        ? (planSelections[activePlanResolution.groupId] ?? {})
        : {};
    const allPlansRemapped = activePlanResolution?.plans?.length > 0
        && activePlanResolution.plans.every((plan) => planRemapSelections[plan.index]);

    const alertTitle = activeAccountResolution
        ? "Account not found"
        : activePlanResolution
            ? "Benefit plan not found"
            : activeCoverageResolution
                ? "Coverage tier set required"
                : "We couldn't process this file";
    const showRetryFileHint = !activeAccountResolution && !activePlanResolution && !activeCoverageResolution;

    return (
        <div className="billing-rate-builder__step container">
            <div className="billing-rate-builder__mode-tabs">
                <button
                    type="button"
                    className={`billing-rate-builder__mode-tab${mode === "upload" ? " billing-rate-builder__mode-tab--active" : ""}`}
                    onClick={() => setMode("upload")}
                >
                    Upload Files
                </button>
                <button
                    type="button"
                    className={`billing-rate-builder__mode-tab${mode === "submissions" ? " billing-rate-builder__mode-tab--active" : ""}`}
                    onClick={() => setMode("submissions")}
                >
                    Submissions
                </button>
            </div>

            {mode === "upload" && (
                <div key="upload" className="billing-rate-builder__mode-panel">
                <div className="billing-rate-builder__upload-cards">

                    {/* Card 1 — New Submission */}
                    <div className="billing-rate-builder__upload-card">
                        <div className="billing-rate-builder__upload-card-header">
                            <span className="billing-rate-builder__upload-card-title">New Submission</span>
                            <span className="billing-rate-builder__upload-card-desc">
                                Upload source files to extract billing rates using AI.
                            </span>
                        </div>

                        <div className="billing-rate-builder__plan-type-selector billing-rate-builder__plan-type-selector--inline">
                            {["level-funded", "aso"].map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`billing-rate-builder__plan-type-btn${planType === type ? " billing-rate-builder__plan-type-btn--active" : ""}`}
                                    onClick={() => setPlanType(type)}
                                >
                                    {type === "level-funded" ? "Level-Funded" : "ASO"}
                                </button>
                            ))}
                        </div>

                        {planType === "level-funded" && (
                            <>
                                <p className="billing-rate-builder__upload-card-hint">
                                    Import a Sold Rates workbook. The file is parsed locally — no upload required.
                                </p>
                                <div className="billing-rate-builder__upload-card-action">
                                    <label className="billing-rate-builder__import-label">Sold Rates (XLSX / XLSM)</label>
                                    <button
                                        type="button"
                                        className="btn edit-btn"
                                        onClick={() => xlsxInputRef.current?.click()}
                                        disabled={filePickerLoading === "xlsx"}
                                    >
                                        {filePickerLoading === "xlsx" ? "Loading…" : "Choose File"}
                                    </button>
                                </div>
                            </>
                        )}

                        {planType === "aso" && (
                            <>
                                <p className="billing-rate-builder__upload-card-hint">
                                    How are these rates coming in?
                                </p>
                                <div className="billing-rate-builder__source-selector" role="radiogroup" aria-label="ASO rate source">
                                    <button
                                        type="button"
                                        role="radio"
                                        aria-checked={asoSource === "spreadsheet"}
                                        className={`billing-rate-builder__source-tile${asoSource === "spreadsheet" ? " billing-rate-builder__source-tile--active" : ""}`}
                                        onClick={() => setAsoSource("spreadsheet")}
                                    >
                                        <span className="billing-rate-builder__source-tile-title">Sold Rates workbook</span>
                                        <span className="billing-rate-builder__source-tile-desc">Parsed locally — no upload</span>
                                    </button>
                                    <button
                                        type="button"
                                        role="radio"
                                        aria-checked={asoSource === "documents"}
                                        className={`billing-rate-builder__source-tile${asoSource === "documents" ? " billing-rate-builder__source-tile--active" : ""}`}
                                        onClick={() => setAsoSource("documents")}
                                    >
                                        <span className="billing-rate-builder__source-tile-title">PDF documents</span>
                                        <span className="billing-rate-builder__source-tile-desc">ASA, stop loss, renewal — via AI</span>
                                    </button>
                                </div>

                                {asoSource === "spreadsheet" && (
                                    <div className="billing-rate-builder__upload-card-action">
                                        <label className="billing-rate-builder__import-label">Sold Rates (XLSX / XLSM)</label>
                                        <button
                                            type="button"
                                            className="btn edit-btn"
                                            onClick={() => asoXlsxInputRef.current?.click()}
                                            disabled={filePickerLoading === "aso-xlsx"}
                                        >
                                            {filePickerLoading === "aso-xlsx" ? "Loading…" : "Choose File"}
                                        </button>
                                    </div>
                                )}

                                {asoSource === "documents" && (
                                    <>
                                <div className="billing-rate-builder__upload-card-action">
                                    <label className="billing-rate-builder__import-label">
                                        Client ID <span className="billing-rate-builder__import-hint">type to search HRP</span>
                                    </label>
                                    <div className="billing-rate-builder__client-search-wrap" ref={asoWrapperRef}>
                                        <input
                                            type="text"
                                            className="billing-rate-builder__client-name-input"
                                            placeholder="Type client ID to search…"
                                            value={asoClientName}
                                            onChange={handleAsoClientNameChange}
                                            onFocus={() => asoDropdownItems.length > 0 && setAsoDropdownOpen(true)}
                                            autoComplete="off"
                                        />
                                        {asoDropdownLoading && (
                                            <span className="billing-rate-builder__client-search-loading">…</span>
                                        )}
                                        {asoDropdownOpen && asoDropdownItems.length > 0 && (
                                            <ul className="billing-rate-builder__client-dropdown">
                                                {asoDropdownItems.map((row) => (
                                                    <li
                                                        key={row.accountKey ?? row.accountHccId}
                                                        className="billing-rate-builder__client-dropdown-item"
                                                        onMouseDown={() => handleAsoClientSelect(row)}
                                                    >
                                                        {row.accountName}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="billing-rate-builder__upload-card-action">
                                    <label className="billing-rate-builder__import-label">
                                        Documents <span className="billing-rate-builder__import-hint">ASA, stop loss, renewal (PDF)</span>
                                    </label>
                                    <button
                                        type="button"
                                        className="btn edit-btn"
                                        onClick={() => {
                                            const match = findLatestAsoGroupForClient(asoClientName, items);
                                            if (match) {
                                                setDuplicateCheck({ step: "choice", latestGroup: match });
                                            } else {
                                                pdfInputRef.current?.click();
                                            }
                                        }}
                                        disabled={!asoSelectedAccount || filePickerLoading === "aso"}
                                        title={!asoSelectedAccount ? "Select a client above before choosing files" : undefined}
                                    >
                                        {filePickerLoading === "aso" ? "Uploading…" : "Choose Files"}
                                    </button>
                                    {!asoSelectedAccount && (
                                        <span className="billing-rate-builder__import-hint billing-rate-builder__import-hint--warning">
                                            Select a client first
                                        </span>
                                    )}
                                </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    {/* Card 2 — Resume from File */}
                    <div className="billing-rate-builder__upload-card">
                        <div className="billing-rate-builder__upload-card-header">
                            <span className="billing-rate-builder__upload-card-title">Resume from File</span>
                            <span className="billing-rate-builder__upload-card-desc">
                                Load a previously exported billing rate file to continue from where you left off.
                            </span>
                        </div>
                        <div className="billing-rate-builder__upload-card-action">
                            <button
                                type="button"
                                className="btn edit-btn"
                                onClick={() => importJsonInputRef.current?.click()}
                                disabled={filePickerLoading === "json"}
                            >
                                {filePickerLoading === "json" ? "Loading…" : "Choose File"}
                            </button>
                        </div>
                    </div>

                </div>
                </div>
            )}

            {mode === "submissions" && (
                <div key="submissions" className="billing-rate-builder__mode-panel">
                    {alertMessages.length > 0 && (
                        <div className="billing-rate-builder__alert billing-rate-builder__alert--danger">
                            <p className="title billing-rate-builder__alert-title">{alertTitle}</p>
                            {alertMessages.map((message, idx) => (
                                <p key={idx} className="billing-rate-builder__alert-message">{message}</p>
                            ))}
                            {showRetryFileHint && (
                                <p className="billing-rate-builder__alert-message">Please try another file.</p>
                            )}
                        </div>
                    )}

                    {soldPlanCountPrompt?.entries?.[0] && (
                        <div className="billing-rate-builder__coverage-tier-panel">
                            <p className="billing-rate-builder__coverage-tier-panel-title">
                                <strong>{soldPlanCountPrompt.entries[0].name}</strong> has no value in the # of sold plans cell.
                                Enter how many plans to import, then continue.
                            </p>
                            <div className="billing-rate-builder__coverage-tier-row">
                                <label className="billing-rate-builder__coverage-tier-label" htmlFor="sold-plan-count">
                                    Number of sold plans
                                </label>
                                <input
                                    id="sold-plan-count"
                                    className="billing-rate-builder__coverage-tier-select"
                                    type="number"
                                    min={1}
                                    max={MAX_SOLD_PLAN_COUNT}
                                    step={1}
                                    inputMode="numeric"
                                    value={soldPlanCountPrompt.value ?? ""}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        setSoldPlanCountPrompt((current) => current && ({ ...current, value, error: null }));
                                    }}
                                />
                            </div>
                            {soldPlanCountPrompt.error && (
                                <p className="billing-rate-builder__coverage-tier-panel-title">{soldPlanCountPrompt.error}</p>
                            )}
                            <button
                                type="button"
                                className="btn edit-btn billing-rate-builder__coverage-tier-continue"
                                disabled={soldPlanCountPrompt.submitting || !Number.isInteger(Number(soldPlanCountPrompt.value)) || Number(soldPlanCountPrompt.value) < 1 || Number(soldPlanCountPrompt.value) > MAX_SOLD_PLAN_COUNT}
                                onClick={handleSoldPlanCountContinue}
                            >
                                {soldPlanCountPrompt.submitting ? "Continuing…" : "Continue"}
                            </button>
                        </div>
                    )}

                    {activeAccountResolution && (
                        <div className="billing-rate-builder__coverage-tier-panel billing-rate-builder__account-resolution-panel">
                            <p className="billing-rate-builder__coverage-tier-panel-title">
                                Search for the correct account in HRP, then continue.
                            </p>
                            <div className="billing-rate-builder__client-search-wrap" ref={accountSearchWrapperRef}>
                                <input
                                    type="text"
                                    className="billing-rate-builder__client-name-input"
                                    placeholder="Type account name to search…"
                                    value={accountSearchTerm}
                                    onChange={handleAccountSearchChange}
                                    onFocus={() => accountSearchItems.length > 0 && setAccountSearchOpen(true)}
                                    autoComplete="off"
                                />
                                {accountSearchLoading && (
                                    <span className="billing-rate-builder__client-search-loading">…</span>
                                )}
                                {accountSearchOpen && accountSearchItems.length > 0 && (
                                    <ul className="billing-rate-builder__client-dropdown">
                                        {accountSearchItems.map((row) => (
                                            <li
                                                key={row.accountKey ?? row.accountHccId}
                                                className="billing-rate-builder__client-dropdown-item"
                                                onMouseDown={() => handleAccountSearchSelect(
                                                    activeAccountResolution.groupId,
                                                    row,
                                                )}
                                            >
                                                {row.accountName}
                                                {row.accountHccId ? ` (${row.accountHccId})` : ""}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn edit-btn billing-rate-builder__coverage-tier-continue"
                                disabled={!accountSelected || proceedingClient === activeAccountResolution.groupId}
                                onClick={() => handleAccountContinue(activeAccountResolution.groupId)}
                            >
                                {proceedingClient === activeAccountResolution.groupId ? "Continuing…" : "Continue"}
                            </button>
                        </div>
                    )}

                    {activePlanResolution && (
                        <div className="billing-rate-builder__coverage-tier-panel billing-rate-builder__plan-remap-panel">
                            <p className="billing-rate-builder__coverage-tier-panel-title">
                                Map each workbook plan to a benefit plan on this account, then continue.
                            </p>
                            {activePlanResolution.plans.map((plan) => {
                                const planLabel = plan.planName && plan.benefitPlanHccId
                                    ? `${plan.planName} (${plan.benefitPlanHccId})`
                                    : (plan.planName ?? plan.benefitPlanHccId ?? `Plan ${plan.index}`);
                                const selectId = `plan-remap-${activePlanResolution.groupId}-${plan.index}`;
                                return (
                                    <div
                                        key={plan.index}
                                        className="billing-rate-builder__coverage-tier-row"
                                    >
                                        <label className="billing-rate-builder__coverage-tier-label" htmlFor={selectId}>
                                            {planLabel}
                                        </label>
                                        <select
                                            id={selectId}
                                            className="billing-rate-builder__coverage-tier-select"
                                            value={planRemapSelections[plan.index]?.benefitPlanHccId ?? ""}
                                            onChange={(e) => handlePlanSelect(
                                                activePlanResolution.groupId,
                                                plan.index,
                                                (activePlanResolution.options ?? []).find(
                                                    (opt) => opt.benefitPlanHccId === e.target.value,
                                                ) ?? null,
                                            )}
                                        >
                                            <option value="">Select benefit plan…</option>
                                            {(activePlanResolution.options ?? []).map((opt) => (
                                                <option
                                                    key={opt.benefitPlanHccId}
                                                    value={opt.benefitPlanHccId}
                                                >
                                                    {opt.benefitPlanName
                                                        ? `${opt.benefitPlanName} (${opt.benefitPlanHccId})`
                                                        : opt.benefitPlanHccId}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                className="btn edit-btn billing-rate-builder__coverage-tier-continue"
                                disabled={!allPlansRemapped || proceedingClient === activePlanResolution.groupId}
                                onClick={() => handlePlanContinue(activePlanResolution.groupId)}
                            >
                                {proceedingClient === activePlanResolution.groupId ? "Continuing…" : "Continue"}
                            </button>
                        </div>
                    )}

                    {activeCoverageResolution && (
                        <div className="billing-rate-builder__coverage-tier-panel">
                            <p className="billing-rate-builder__coverage-tier-panel-title">
                                Select a coverage tier set for each benefit plan, then continue.
                            </p>
                            {activeCoverageResolution.plans.map((plan) => {
                                const planLabel = plan.name ?? plan.benefitPlanHccId ?? `Plan ${plan.index}`;
                                const selectId = `coverage-tier-${activeCoverageResolution.groupId}-${plan.benefitPlanHccId}`;
                                return (
                                    <div
                                        key={plan.benefitPlanHccId}
                                        className="billing-rate-builder__coverage-tier-row"
                                    >
                                        <label className="billing-rate-builder__coverage-tier-label" htmlFor={selectId}>
                                            {planLabel}
                                        </label>
                                        <select
                                            id={selectId}
                                            className="billing-rate-builder__coverage-tier-select"
                                            value={resolutionSelections[plan.benefitPlanHccId] ?? ""}
                                            onChange={(e) => handleCoverageTierSelect(
                                                activeCoverageResolution.groupId,
                                                plan.benefitPlanHccId,
                                                e.target.value,
                                            )}
                                        >
                                            <option value="">Select coverage tier set…</option>
                                            {COVERAGE_TIER_SET_OPTIONS.map((name) => (
                                                <option key={name} value={name}>{name}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                className="btn edit-btn billing-rate-builder__coverage-tier-continue"
                                disabled={!allCoveragePlansSelected || proceedingClient === activeCoverageResolution.groupId}
                                onClick={() => {
                                    const clientItems = items.filter((i) => i.groupId === activeCoverageResolution.groupId);
                                    handleCoverageTierContinue(activeCoverageResolution.groupId, clientItems);
                                }}
                            >
                                {proceedingClient === activeCoverageResolution.groupId ? "Continuing…" : "Continue"}
                            </button>
                        </div>
                    )}

                    <div className="billing-rate-builder__table-toolbar">
                        <div className="billing-rate-builder__table-filter">
                            <span className="billing-rate-builder__table-filter-label">Filter:</span>
                            <div className="billing-rate-builder__plan-type-selector billing-rate-builder__plan-type-selector--inline">
                                {["all", "level-funded", "aso"].map((f) => (
                                    <button
                                        key={f}
                                        type="button"
                                        className={`billing-rate-builder__plan-type-btn${tableFilter === f ? " billing-rate-builder__plan-type-btn--active" : ""}`}
                                        onClick={() => { setTableFilter(f); setInProgressPage(1); setCompletedPage(1); }}
                                    >
                                        {f === "all" ? "All" : f === "level-funded" ? "Level-Funded" : "ASO"}
                                    </button>
                                ))}
                            </div>
                            <span className="billing-rate-builder__table-count">
                                {filteredGrouped.size} {filteredGrouped.size === 1 ? "submission" : "submissions"}
                            </span>
                        </div>
                        <div className="billing-rate-builder__table-actions">
                            <button type="button" className="btn billing-rate-builder__table-action-btn" onClick={handleRefresh}>
                                <IoRefreshOutline size={15} />
                                Refresh
                            </button>
                        </div>
                    </div>

                    <div className="billing-rate-builder__processing-table-wrap">
                        <table className="billing-rate-builder__processing-table">
                            <thead>
                                <tr>
                                    <th>Client</th>
                                    <th>Type</th>
                                    <th>Date</th>
                                    <th>Files</th>
                                    <th>Done</th>
                                    <th>Status</th>
                                    <th>By</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredGrouped.size === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="billing-rate-builder__list-empty">
                                            {grouped.size === 0 ? "No files imported yet." : "No submissions match the selected filter."}
                                        </td>
                                    </tr>
                                ) : (() => {
                                    const renderGroupRows = ([groupId, clientItems]) => {
                                        const asyncStatus = submissionStatusByGroupId[groupId];
                                        const isAsyncGroup = asyncStatus !== undefined;
                                        const doneCount = clientItems.filter((i) => i.status === STATUS.DONE || i.status === STATUS.SUBMITTED).length;
                                        const processingCount = clientItems.filter((i) => i.status === STATUS.PROCESSING).length;
                                        const errorCount = clientItems.filter((i) => i.status === STATUS.ERROR).length;
                                        const isExpanded = expandedClients.has(groupId);

                                        const overallStatus = (submittedGroupIds.has(groupId) || clientItems.every((i) => i.status === STATUS.SUBMITTED))
                                            ? STATUS.SUBMITTED
                                            : isAsyncGroup
                                            ? asyncStatus === "EXTRACTION_COMPLETE" ? STATUS.DONE
                                                : asyncStatus === "FAILED" ? STATUS.ERROR
                                                : STATUS.PROCESSING
                                            : processingCount > 0
                                                ? STATUS.PROCESSING
                                                : errorCount > 0 && doneCount === 0
                                                    ? STATUS.ERROR
                                                    : doneCount === clientItems.length
                                                        ? STATUS.DONE
                                                        : STATUS.QUEUED;

                                        const isProceeding = proceedingClient === groupId;
                                        const parsedItems = clientItems.filter((i) => i.parsed != null);
                                        const canProceed = asyncStatus === "EXTRACTION_COMPLETE"
                                            || (!isAsyncGroup
                                                && parsedItems.length > 0
                                                && parsedItems.length === clientItems.length
                                                && !clientItems.some((i) => i.status === STATUS.PROCESSING));

                                        const historyItems = clientItems.filter((i) => i.source === "history");
                                        const showDelete = canDeleteSubmission(clientItems);
                                        const isLevelFunded = clientItems[0]?.planType === "level-funded";
                                        // ASO Sold Rates groups are locally parsed like LF; they must not expose the
                                        // async-only affordances (add files / reprocess through Textract).
                                        const usesLocalExtraction = isLevelFunded
                                            || (clientItems.length > 0 && clientItems.every((i) => i.extractionMode === "spreadsheet"));
                                        const canManageFiles = canManageAsoFiles({
                                            isLevelFunded: usesLocalExtraction,
                                            overallStatus,
                                            isAsyncGroup,
                                            asyncStatus,
                                            clientItems,
                                        });
                                        const isReprocessing = historyItems.some((i) => i.status === STATUS.PROCESSING);
                                        const isJsonImport = clientItems.some((i) => i.type === "json");
                                        const showReprocess = historyItems.length > 0 && !isReprocessing && !isAsyncGroup && !usesLocalExtraction && !isJsonImport;
                                        const displayName = clientItems[0]?.clientName || (groupId ? groupId.slice(0, 8) + "…" : "—");
                                        const asyncSk = clientItems.find((i) => i.dbRecord?.sk)?.dbRecord?.sk;
                                        const matchingDraftMeta = draftsMeta[groupId] ?? (asyncSk ? draftsMeta[asyncSk] : null) ?? null;
                                        const matchingDraftId = matchingDraftMeta ? (draftsMeta[groupId] ? groupId : asyncSk) : null;

                                        return [
                                            <tr key={groupId} className="billing-rate-builder__client-row">
                                                <td>
                                                    <div className="billing-rate-builder__client-name-cell">
                                                        <button
                                                            type="button"
                                                            className="billing-rate-builder__expand-btn"
                                                            onClick={() => toggleClient(groupId)}
                                                            aria-expanded={isExpanded}
                                                        >
                                                            <IoChevronDown
                                                                className={`billing-rate-builder__expand-chevron${isExpanded ? " billing-rate-builder__expand-chevron--open" : ""}`}
                                                            />
                                                            {displayName}
                                                        </button>
                                                        {matchingDraftId && overallStatus !== STATUS.SUBMITTED && (
                                                            <span
                                                                className="billing-rate-builder__draft-badge"
                                                                title={`Draft saved ${matchingDraftMeta?.savedAt ? new Date(matchingDraftMeta.savedAt).toLocaleString() : ""}`}
                                                            >
                                                                DRAFT
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{isLevelFunded ? "Level-Funded" : clientItems[0]?.planType === "aso" ? "ASO" : "—"}</td>
                                                <td className="billing-rate-builder__muted">
                                                    {clientItems[0]?.createdAt.toLocaleString()}
                                                </td>
                                                <td>{clientItems.length}</td>
                                                <td>{doneCount} / {clientItems.length}</td>
                                                <td><StatusBadge status={overallStatus} /></td>
                                                <td className="billing-rate-builder__muted">{clientItems[0]?.submittedBy || "—"}</td>
                                                <td><div className="billing-rate-builder__client-row-actions">
                                                    {(canProceed || matchingDraftId) && overallStatus !== STATUS.SUBMITTED && (matchingDraftId || !(usesLocalExtraction && mergedClients.has(groupId))) ? (
                                                        <button
                                                            type="button"
                                                            className="btn edit-btn"
                                                            disabled={isProceeding}
                                                            onClick={() => handleProcessClick(groupId, clientItems, matchingDraftId, {
                                                                allowStartFresh: !usesLocalExtraction && !isJsonImport,
                                                            })}
                                                        >
                                                            {isProceeding ? "Processing…" : "Process"}
                                                        </button>
                                                    ) : (
                                                        <span aria-hidden="true" />
                                                    )}
                                                    <RowMenu
                                                        items={overallStatus === STATUS.SUBMITTED ? [
                                                            showDelete && {
                                                                label: "Delete",
                                                                onClick: () => handleDelete(groupId, clientItems),
                                                                danger: true,
                                                            },
                                                        ].filter(Boolean) : [
                                                            isAsyncGroup && (asyncStatus === "PENDING" || asyncStatus === "PROCESSING") && asyncSk && {
                                                                label: "Refresh",
                                                                onClick: () => handleManualRefreshGroup(groupId, asyncSk),
                                                            },
                                                            showReprocess && {
                                                                label: "Reprocess",
                                                                onClick: () => handleReprocessGroupAsync(clientItems),
                                                            },
                                                            !usesLocalExtraction && {
                                                                label: "Add files",
                                                                onClick: () => handleReImport(groupId, clientItems[0]?.clientName, clientItems[0]?.type),
                                                            },
                                                            showDelete && {
                                                                label: "Delete",
                                                                onClick: () => handleDelete(groupId, clientItems),
                                                                danger: true,
                                                            },
                                                        ].filter(Boolean)}
                                                    />
                                                </div></td>
                                            </tr>,
                                            isExpanded && clientItems.map((item) => (
                                                <tr key={item.id} className="billing-rate-builder__file-row">
                                                    <td className="billing-rate-builder__processing-table-name" title={item.name}>
                                                        <span className="billing-rate-builder__file-indent" />
                                                        {item.name}
                                                    </td>
                                                    <td>{(item.type ?? "").toUpperCase()}</td>
                                                    <td></td>
                                                    <td></td>
                                                    <td></td>
                                                    <td><StatusBadge status={item.status} /></td>
                                                    <td className="billing-rate-builder__muted">{item.submittedBy || "—"}</td>
                                                    <td className="billing-rate-builder__file-row-actions-cell">
                                                        <div className="billing-rate-builder__file-row-actions">
                                                            {canManageFiles && canRemoveAsoFile(item, canManageFiles) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn edit-btn billing-rate-builder__file-action-btn"
                                                                    disabled={fileDeleteLoading}
                                                                    onClick={() => handleRemoveFile(groupId, item, clientItems)}
                                                                >
                                                                    Remove
                                                                </button>
                                                            )}
                                                            {item.status === STATUS.ERROR && (
                                                                <span
                                                                    className="billing-rate-builder__error-msg"
                                                                    title={item.errorMsg}
                                                                >
                                                                    {item.errorMsg}
                                                                </span>
                                                            )}
                                                            {!usesLocalExtraction && !item.isAsyncSubmission && item.type !== "json" && (
                                                                (item.source === "session" && (item.status === STATUS.DONE || item.status === STATUS.ERROR)) ||
                                                                (item.source === "history" && (item.s3Keys ?? []).length > 0)
                                                            ) && (
                                                                <button
                                                                    type="button"
                                                                    className="btn edit-btn billing-rate-builder__reprocess-btn"
                                                                    disabled={item.status === STATUS.PROCESSING}
                                                                    onClick={() => handleReprocess(item)}
                                                                >
                                                                    Reprocess
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )),
                                        ];
                                    };

                                    return <>
                                        <tr className="billing-rate-builder__section-divider">
                                            <td colSpan={8}>
                                                <button
                                                    type="button"
                                                    className="billing-rate-builder__section-divider-btn"
                                                    onClick={() => setInProgressSectionCollapsed((v) => !v)}
                                                >
                                                    <IoChevronDown
                                                        className={`billing-rate-builder__expand-chevron${inProgressSectionCollapsed ? "" : " billing-rate-builder__expand-chevron--open"}`}
                                                    />
                                                    In Progress
                                                    <span className="billing-rate-builder__section-count">{inProgressEntries.length}</span>
                                                </button>
                                            </td>
                                        </tr>
                                        {!inProgressSectionCollapsed && (inProgressEntries.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="billing-rate-builder__list-empty">
                                                    No in-progress submissions.
                                                </td>
                                            </tr>
                                        ) : inProgressPageEntries.map(renderGroupRows))}
                                        {!inProgressSectionCollapsed && inProgressPageCount > 1 && (
                                            <tr>
                                                <td colSpan={8}>
                                                    <div className="billing-rate-builder__pagination">
                                                        <button type="button" className="btn billing-rate-builder__pagination-btn" disabled={inProgressPage === 1} onClick={() => setInProgressPage((p) => p - 1)}>‹</button>
                                                        <span className="billing-rate-builder__pagination-label">{inProgressPage} / {inProgressPageCount}</span>
                                                        <button type="button" className="btn billing-rate-builder__pagination-btn" disabled={inProgressPage === inProgressPageCount} onClick={() => setInProgressPage((p) => p + 1)}>›</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        {completedEntries.length > 0 && <>
                                            <tr className="billing-rate-builder__section-divider">
                                                <td colSpan={8}>
                                                    <button
                                                        type="button"
                                                        className="billing-rate-builder__section-divider-btn"
                                                        onClick={() => setCompletedSectionCollapsed((v) => !v)}
                                                    >
                                                        <IoChevronDown
                                                            className={`billing-rate-builder__expand-chevron${completedSectionCollapsed ? "" : " billing-rate-builder__expand-chevron--open"}`}
                                                        />
                                                        Completed
                                                        <span className="billing-rate-builder__section-count">{completedEntries.length}</span>
                                                    </button>
                                                </td>
                                            </tr>
                                            {!completedSectionCollapsed && completedPageEntries.map(renderGroupRows)}
                                            {!completedSectionCollapsed && completedPageCount > 1 && (
                                                <tr>
                                                    <td colSpan={8}>
                                                        <div className="billing-rate-builder__pagination">
                                                            <button type="button" className="btn billing-rate-builder__pagination-btn" disabled={completedPage === 1} onClick={() => setCompletedPage((p) => p - 1)}>‹</button>
                                                            <span className="billing-rate-builder__pagination-label">{completedPage} / {completedPageCount}</span>
                                                            <button type="button" className="btn billing-rate-builder__pagination-btn" disabled={completedPage === completedPageCount} onClick={() => setCompletedPage((p) => p + 1)}>›</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </>}
                                    </>;
                                })()}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <input ref={xlsxInputRef} type="file" accept={ACCEPTED_XLSX} multiple hidden onChange={handleXlsxChange} />
            <input ref={asoXlsxInputRef} type="file" accept={ACCEPTED_ASO_XLSX} multiple hidden onChange={handleAsoXlsxChange} />
            <input ref={pdfInputRef} type="file" accept={ACCEPTED_ASO_FILES} multiple hidden onChange={handlePdfChange} />
            <input
                ref={importJsonInputRef}
                type="file"
                accept=".json,application/json"
                style={{ display: "none" }}
                onChange={handleImportJsonFileChange}
            />

            <Modal isOpen={draftCheckPending != null} onClose={() => setDraftCheckPending(null)}>
                <div className="billing-rate-builder__delete-modal">
                    <h3 className="billing-rate-builder__delete-modal-title">Draft Found</h3>
                    <p className="billing-rate-builder__delete-modal-description">
                        A saved draft was found for <strong>{draftCheckPending?.clientItems?.[0]?.clientName ?? "this client"}</strong>.
                        Would you like to continue from the draft{draftCheckPending?.allowStartFresh ? " or start fresh" : ""}?
                    </p>
                    <div className="billing-rate-builder__delete-modal-actions">
                        <button type="button" className="btn edit-btn" onClick={() => setDraftCheckPending(null)}>
                            Cancel
                        </button>
                        {draftCheckPending?.allowStartFresh && (
                            <button
                                type="button"
                                className="btn edit-btn"
                                onClick={() => {
                                    const { groupId, clientItems } = draftCheckPending;
                                    setDraftCheckPending(null);
                                    handleProceed(groupId, clientItems);
                                }}
                            >
                                Start Fresh
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn edit-btn"
                            onClick={() => {
                                const { draftId, isJsonImport } = draftCheckPending;
                                setDraftCheckPending(null);
                                onResumeDraft?.(draftId, { refreshHrp: isJsonImport === true });
                            }}
                        >
                            Continue from Draft
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={duplicateCheck != null} onClose={() => setDuplicateCheck(null)}>
                <div className="billing-rate-builder__delete-modal">
                    {duplicateCheck && (() => {
                        const { latestGroup } = duplicateCheck;
                        const infoBlock = (
                            <div className="billing-rate-builder__duplicate-submission-info">
                                <div><span className="billing-rate-builder__muted">Client:</span> {latestGroup.clientName}</div>
                                <div><span className="billing-rate-builder__muted">Date:</span> {latestGroup.createdAt.toLocaleString()}</div>
                                <div><span className="billing-rate-builder__muted">Submitted by:</span> {latestGroup.submittedBy || "—"}</div>
                                <div><span className="billing-rate-builder__muted">Ref:</span> <code>{latestGroup.ref}…</code></div>
                                <div>
                                    <span className="billing-rate-builder__muted">Files ({latestGroup.fileNames.length}):</span>
                                    <ul className="billing-rate-builder__duplicate-file-list">
                                        {latestGroup.fileNames.map((n) => <li key={n}>{n}</li>)}
                                    </ul>
                                </div>
                            </div>
                        );
                        if (duplicateCheck.step === "choice") return (
                            <>
                                <h3 className="billing-rate-builder__delete-modal-title">Existing Submission Found</h3>
                                <p className="billing-rate-builder__delete-modal-description">
                                    A submission already exists for <strong>{latestGroup.clientName}</strong>.
                                    Would you like to update it with new files, or create a separate submission?
                                </p>
                                {infoBlock}
                                <div className="billing-rate-builder__delete-modal-actions">
                                    <button type="button" className="btn edit-btn" onClick={() => setDuplicateCheck(null)}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn edit-btn"
                                        onClick={() => {
                                            setDuplicateCheck(null);
                                            pdfInputRef.current?.click();
                                        }}
                                    >
                                        Create New
                                    </button>
                                    <button
                                        type="button"
                                        className="btn edit-btn"
                                        onClick={() => setDuplicateCheck((prev) => ({ ...prev, step: "confirm-update" }))}
                                    >
                                        Update Latest
                                    </button>
                                </div>
                            </>
                        );
                        if (duplicateCheck.step === "confirm-update") return (
                            <>
                                <h3 className="billing-rate-builder__delete-modal-title">Confirm Update</h3>
                                <p className="billing-rate-builder__delete-modal-description">
                                    You are about to append new files to the submission below. Please confirm this is the correct one.
                                </p>
                                {infoBlock}
                                <p className="billing-rate-builder__delete-modal-warning">
                                    Existing files will not be removed, but uploading a file with the same name will overwrite it.
                                </p>
                                <div className="billing-rate-builder__delete-modal-actions">
                                    <button
                                        type="button"
                                        className="btn edit-btn"
                                        onClick={() => setDuplicateCheck((prev) => ({ ...prev, step: "choice" }))}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        className="btn edit-btn"
                                        onClick={() => {
                                            setPendingGroupId(latestGroup.groupId);
                                            setPendingAddClientName(latestGroup.clientName ?? "");
                                            setDuplicateCheck(null);
                                            pdfInputRef.current?.click();
                                        }}
                                    >
                                        Confirm & Choose Files
                                    </button>
                                </div>
                            </>
                        );
                        return null;
                    })()}
                </div>
            </Modal>

<Modal isOpen={deleteConfirm != null} onClose={deleteLoading ? undefined : () => setDeleteConfirm(null)}>
                <div className="billing-rate-builder__delete-modal">
                    <h3 className="billing-rate-builder__delete-modal-title">Delete Submission</h3>
                    <p className="billing-rate-builder__delete-modal-description">
                        Are you sure you want to delete the submission for <strong>{deleteConfirm?.displayName}</strong>?
                    </p>
                    <p className="billing-rate-builder__delete-modal-warning">
                        This will permanently remove all associated files from S3 and the submission record from the database.
                        This action cannot be undone and all data will be unrecoverable.
                    </p>
                    <div className="billing-rate-builder__delete-modal-actions">
                        <button type="button" className="btn edit-btn" onClick={() => setDeleteConfirm(null)} disabled={deleteLoading}>
                            Cancel
                        </button>
                        <button type="button" className="btn delete-btn" onClick={confirmDelete} disabled={deleteLoading}>
                            {deleteLoading ? "Deleting…" : "Delete"}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal isOpen={fileDeleteConfirm != null} onClose={fileDeleteLoading ? undefined : () => setFileDeleteConfirm(null)}>
                <div className="billing-rate-builder__delete-modal">
                    <h3 className="billing-rate-builder__delete-modal-title">Remove File</h3>
                    <p className="billing-rate-builder__delete-modal-description">
                        Remove <strong>{fileDeleteConfirm?.displayName}</strong> from this submission?
                    </p>
                    <p className="billing-rate-builder__delete-modal-warning">
                        The file will be deleted from storage. If you have a draft for this submission, it will be cleared and extraction will be recomputed from the remaining files.
                    </p>
                    <div className="billing-rate-builder__delete-modal-actions">
                        <button type="button" className="btn edit-btn" onClick={() => setFileDeleteConfirm(null)} disabled={fileDeleteLoading}>
                            Cancel
                        </button>
                        <button type="button" className="btn delete-btn" onClick={confirmRemoveFile} disabled={fileDeleteLoading}>
                            {fileDeleteLoading ? "Removing…" : "Remove"}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default BillingRateBuilderStepOne;
